import { FUNDING_V2_ENABLED } from '@/utils/funding-mode';
import { ref, shallowRef, computed } from 'vue';
import { getWalletProviderId, hasAuthSession } from '@/utils/auth-token';
import { getAuthGeneration } from '@/utils/auth-teardown';
import { resolveTradeUserAddress } from '@/utils/resolve-wallet-address';
import { ensurePrivyEmbeddedReady } from '@/utils/privy';
import { coboDepositOrders } from '@/api/trade';
import {
  endAnalyticsFunnelSession,
  trackAnalyticsLog,
  withDepositFunnelValue,
} from '@/utils/analytics-track';
import {
  afterDepositFailTracked,
  afterDepositSuccessTracked,
} from '@/utils/analytics-conversion-extras';
import { markDepositBonusSuccessful } from '@/stores/depositBonusPrompt';
import {
  armDepositChainDwell,
  setDepositChainDwellDialogOpen,
} from '@/utils/analytics-deposit-chain-dwell';
import {
  POLL_PHASE,
  clearDepositWatchSession,
  fetchBlockNumber,
  fetchFromBlockWithLookback,
  fetchTransactionReceipt,
  isReceiptFailed,
  isReceiptSuccess,
  loadDepositWatchSession,
  pollIncomingTransfer,
  pollTxReceipt,
  resolveDepositRpcUrl,
  resolveReceiptBlockTimeMs,
  resolveTxBlockTimeMs,
  saveDepositWatchSession,
} from '@/utils/deposit-chain-monitor';
import {
  buildDepositWatchKey,
  normalizeTokenContracts,
  resolveDefaultChainId,
  resolveDepositExplorerUrl,
  resolveDepositWatchContracts,
} from '@/utils/deposit-watch-targets';
import {
  ITEM_PHASE,
  buildDepositItem,
  collectTxHashesFromItems,
  countDepositItems,
  dedupeDepositItemsByTxHash,
  formatDepositAmountLabel,
  hasInProgressItems,
  isCoboDepositItem,
  isItemInProgress,
  isItemTerminal,
  itemUiPhase,
  listVisibleQueueItems,
  loadDepositItemsSnapshot,
  mergeSeenTxLists,
  pickFocusItemId,
  pickNavItem,
  saveDepositItemsSnapshot,
  shouldShowEmbeddedForItem,
  shouldShowNavForItem,
} from '@/utils/deposit-item-queue';
import {
  COBO_ORDER_POLL_MS,
  coboOrderPromptNotBeforeMs,
  collectHistoricalCoboDepositTxids,
  collectHistoricalCoboOrderIds,
  extractCoboCompletedAtMs,
  extractCoboCompletionTxid,
  extractCoboDepositAddress,
  extractCoboDepositChain,
  extractCoboDepositConfirmedAtMs,
  extractCoboDepositCurrency,
  extractCoboDepositTxid,
  extractCoboDisplayAmount,
  extractCoboOrderId,
  extractCoboPrivyAddress,
  findCoboOrderForItem,
  isCoboOrderCompleted,
  isCoboOrderFailed,
  shouldPromptCoboOrder,
  looksLikeTxHash,
  normalizeCoboAddressList,
  normalizeCoboOrdersPage,
  normalizeDepositTokenSymbol,
  normalizeTxHashKey,
  orderCreatedMs,
  pickNewestInProgressCoboOrder,
  pickNewestUnseenTerminalCoboOrder,
  resolveCoboDepositExplorerUrl,
  resolveCoboOrderStatus,
} from '@/utils/cobo-deposit-order';
import {
  clearCoboOrderSuppress,
  loadCoboOrderSuppress,
  mergeCoboOrderSuppress,
} from '@/utils/cobo-order-suppress';

const NAV_TOAST_AUTO_CLOSE_MS = 5000;
/** 关窗后 FAST 冲刺：约 12 × 2.5s ≈ 30s（≈15 个 Polygon 块） */
export const POST_DIALOG_FAST_MAX_TICKS = 12;

const depositItems = ref([]);
const focusItemId = ref(null);
const navVisible = ref(false);
const embeddedVisible = ref(false);
const depositDialogOpen = ref(false);
const depositDialogClosing = ref(false);
const embedAnchorEl = shallowRef(null);

/** 兼容旧组件：由焦点条目派生 */
const phase = computed(() => {
  const item = getFocusItem();
  if (!item) return 'idle';
  return itemUiPhase(item);
});
const amountLabel = computed(() => getFocusItem()?.amountLabel ?? '');
const tokenSymbol = computed(() => getFocusItem()?.tokenSymbol ?? 'USDC');
const watchAddress = computed(() => getFocusItem()?.watchAddress ?? '');
const fromAddress = computed(() => getFocusItem()?.fromAddress ?? '');
const txHash = computed(() => getFocusItem()?.txHash ?? '');
const detectedAt = computed(() => getFocusItem()?.detectedAt ?? getFocusItem()?.createdAt ?? null);
const confirmedAt = computed(() => getFocusItem()?.confirmedAt ?? null);
const chainId = computed(() => getFocusItem()?.chainId ?? resolveDefaultChainId());
const explorerUrl = computed(() => {
  const item = getFocusItem();
  if (!item) return resolveDepositExplorerUrl(resolveDefaultChainId());
  return item.explorerUrl || resolveDepositExplorerUrl(item.chainId);
});

const navItem = computed(() => pickNavItem(depositItems.value, getFocusItem()));
const navPhase = computed(() => (navItem.value ? itemUiPhase(navItem.value) : 'idle'));
const navAmountLabel = computed(() => navItem.value?.amountLabel ?? '');
const navTokenSymbol = computed(() => navItem.value?.tokenSymbol ?? 'USDC');
const navWatchAddress = computed(() => navItem.value?.watchAddress ?? '');
const navTxHash = computed(() => navItem.value?.txHash ?? '');
const navDetectedAt = computed(() => navItem.value?.detectedAt ?? navItem.value?.createdAt ?? null);
const navConfirmedAt = computed(() => navItem.value?.confirmedAt ?? null);
const navExplorerUrl = computed(() => {
  const item = navItem.value;
  if (!item) return resolveDepositExplorerUrl(resolveDefaultChainId());
  return item.explorerUrl || resolveDepositExplorerUrl(item.chainId);
});

function buildDepositPipeline(item) {
  if (!item) return null;
  return {
    depositRail: item.depositRail || 'direct',
    fromAddress: item.fromAddress || '',
    watchAddress: item.watchAddress || '',
    coboAddress: item.coboAddress || (item.depositRail === 'cobo' ? item.watchAddress : '') || '',
    privyAddress: item.privyAddress || item.watchAddress || '',
    txHash: item.txHash || '',
    completionTxHash: looksLikeTxHash(item.completionTxHash) ? item.completionTxHash : '',
    explorerUrl: item.explorerUrl || resolveDepositExplorerUrl(item.chainId),
    completionExplorerUrl:
      item.completionExplorerUrl || resolveDepositExplorerUrl(resolveDefaultChainId()),
    coboOrderStatus: item.coboOrderStatus,
    coboStatusText: item.coboStatusText || '',
    exchangeStatus: item.exchangeStatus,
    coboOnChainConfirmed: Boolean(item.coboOnChainConfirmed),
    detectedAt: item.detectedAt ?? item.createdAt ?? null,
    depositConfirmedAt: item.depositConfirmedAt ?? null,
    confirmedAt: item.confirmedAt ?? null,
    amount: item.amount,
  };
}

const focusPipeline = computed(() => buildDepositPipeline(getFocusItem()));
const navPipeline = computed(() => buildDepositPipeline(navItem.value));

const depositCounts = computed(() => countDepositItems(depositItems.value));
const queueItems = computed(() => listVisibleQueueItems(depositItems.value));

let incomingStop = null;
let incomingGetState = null;
let incomingSetPhase = null;
let incomingMarkSeen = null;
let coboOrderPollTimer = null;
let coboOrderPollInflight = false;
let activeWatchKey = '';
let completeHandler = null;
let bootstrapInflight = null;
let navAutoCloseTimer = null;
let pinnedFocusItemId = null;
let manualFocusItemId = null;
/** 扫码转账面打开时无上限 FAST */
let depositWatchUrgent = false;
/** 关窗后仍有 pending 时的 FAST 剩余 tick */
let postDialogFastTicksLeft = 0;

/** itemId → stop receipt poll */
const receiptStops = new Map();
/** 终态埋点去重（itemId:success|fail / orderId:success|fail） */
const depositTerminalAnalyticsKeys = new Set();
/** transfer_submitted 去重（itemId / txHash） */
const depositTransferSubmittedKeys = new Set();
/** chain_confirmed 去重（itemId / txHash） */
const depositChainConfirmedKeys = new Set();
/** select_network：入账状态卡出现 / 关窗回落所选链，去重（itemId / orderId / txHash / dialog） */
const depositSelectNetworkKeys = new Set();
/** order 扫描：同一 orderId+status 只报一次 */
const depositOrderStatusKeys = new Set();
/** 充值弹窗内当前所选链（关窗回落用） */
let dialogSelectedNetwork = /** @type {{ chain: string, asset?: string } | null} */ (null);
/** 本轮弹窗是否已报过 select_network（状态卡优先，关窗兜底） */
let dialogSelectNetworkReported = false;
/** coboOrderId → { timer, startedAt } 关窗后继续跟到终态（仅埋点） */
const coboAnalyticsFollowups = new Map();
/** 关窗后跟单加长到 30 分钟，覆盖多数手动转账确认 */
const COBO_ANALYTICS_FOLLOWUP_MAX_MS = 30 * 60 * 1000;
/** 登录后补扫近 24h 已完成订单 */
const COBO_SUCCESS_BACKFILL_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const COBO_SUCCESS_BACKFILL_STORAGE_KEY = 'ds-analytics-deposit-success-orders';

function isDepositWatchAllowed(capturedGen) {
  if (!hasAuthSession()) return false;
  if (capturedGen == null) return true;
  return capturedGen === getAuthGeneration();
}

/**
 * 发起转账（有 tx）：同一 item/tx 只打一次。Connected 弹窗内已打过则去重跳过。
 * @param {{ id?: string, amount?: unknown, tokenSymbol?: string, source?: string, txHash?: string, chainId?: unknown }} item
 * @param {Record<string, unknown>} [extra]
 */
function trackDepositTransferSubmittedOnce(item, extra = {}) {
  const itemId = String(item?.id ?? '').trim();
  const hash = String(item?.txHash ?? extra.txHash ?? '').trim();
  const keys = [itemId ? `item:${itemId}` : '', hash ? `tx:${hash}` : ''].filter(Boolean);
  if (!keys.length) return;
  if (keys.some((k) => depositTransferSubmittedKeys.has(k))) return;
  keys.forEach((k) => depositTransferSubmittedKeys.add(k));

  try {
    trackAnalyticsLog({
      key: 'deposit.transfer_submitted',
      label: '链上转账已提交',
      value: withDepositFunnelValue({
        rail: 'crypto',
        amount: item?.amount ?? extra.amount,
        token: item?.tokenSymbol || extra.token || 'USDC',
        source: item?.source || extra.source || undefined,
        txHash: hash || undefined,
        itemId: itemId || undefined,
        chainId: item?.chainId ?? extra.chainId,
        via: extra.via || 'watcher',
        ...extra,
      }),
    });
  } catch (err) {
    console.warn('[deposit-status] transfer_submitted analytics failed', err);
  }
}

/**
 * 链上确认（尚未后台入账）：同一 item/tx 只打一次。
 * @param {{ id?: string, amount?: unknown, tokenSymbol?: string, source?: string, txHash?: string, chainId?: unknown }} item
 * @param {Record<string, unknown>} [extra]
 */
function trackDepositChainConfirmedOnce(item, extra = {}) {
  const itemId = String(item?.id ?? '').trim();
  const hash = String(item?.txHash ?? extra.txHash ?? '').trim();
  const keys = [itemId ? `item:${itemId}` : '', hash ? `tx:${hash}` : ''].filter(Boolean);
  if (!keys.length) return;
  if (keys.some((k) => depositChainConfirmedKeys.has(k))) return;
  keys.forEach((k) => depositChainConfirmedKeys.add(k));

  try {
    trackAnalyticsLog({
      key: 'deposit.chain_confirmed',
      label: '链上确认',
      value: withDepositFunnelValue({
        rail: 'crypto',
        amount: item?.amount ?? extra.amount,
        token: item?.tokenSymbol || extra.token || 'USDC',
        source: item?.source || extra.source || undefined,
        txHash: hash || undefined,
        itemId: itemId || undefined,
        chainId: item?.chainId ?? extra.chainId,
        via: extra.via || undefined,
        ...extra,
      }),
    });
  } catch (err) {
    console.warn('[deposit-status] chain_confirmed analytics failed', err);
  }
}

/**
 * 确认所用链（进 51）：
 * - 入账状态卡（Embedded）首次出现
 * - 或关充值弹窗时回落当前所选链
 * 不再绑在后台 success。
 * @param {{ id?: string, coboOrderId?: string, amount?: unknown, tokenSymbol?: string, source?: string, txHash?: string, chainId?: unknown, chain?: unknown }} item
 * @param {Record<string, unknown>} [extra]
 */
function trackDepositSelectNetworkOnce(item, extra = {}) {
  const itemId = String(item?.id ?? '').trim();
  const orderId = String(item?.coboOrderId ?? extra.orderId ?? '').trim();
  const hash = String(item?.txHash ?? extra.txHash ?? '').trim();
  const chain = String(extra.chain ?? item?.chain ?? item?.chainId ?? extra.chainId ?? '').trim();
  if (!chain) return;
  const keys = [
    itemId ? `item:${itemId}` : '',
    orderId ? `order:${orderId}` : '',
    hash ? `tx:${hash}` : '',
    !itemId && !orderId && !hash ? `dialog:${chain}` : '',
  ].filter(Boolean);
  if (!keys.length) return;
  if (keys.some((k) => depositSelectNetworkKeys.has(k))) return;
  keys.forEach((k) => depositSelectNetworkKeys.add(k));
  dialogSelectNetworkReported = true;

  try {
    trackAnalyticsLog({
      key: 'deposit.select_network',
      label: '确认充值所用链',
      value: withDepositFunnelValue({
        rail: 'crypto',
        chain,
        chainId: item?.chainId ?? extra.chainId,
        asset: item?.tokenSymbol || extra.token || extra.asset || undefined,
        source: item?.source || extra.source || undefined,
        txHash: hash || undefined,
        itemId: itemId || undefined,
        orderId: orderId || undefined,
        phase: extra.phase || 'status_ui',
        via: extra.via || undefined,
      }),
    });
  } catch (err) {
    console.warn('[deposit-status] select_network analytics failed', err);
  }
}

/**
 * Cobo orders 扫描：status 切换上报（本地含 status；51 只计事件名）。
 * @param {{ id?: string, coboOrderId?: string, chainId?: unknown, chain?: unknown, tokenSymbol?: string, source?: string }} item
 * @param {number} status
 * @param {Record<string, unknown>} [extra]
 */
function trackDepositOrderStatusOnce(item, status, extra = {}) {
  const orderId = String(item?.coboOrderId ?? extra.orderId ?? '').trim();
  if (!orderId || !Number.isFinite(status)) return;
  const key = `${orderId}:${status}`;
  if (depositOrderStatusKeys.has(key)) return;
  depositOrderStatusKeys.add(key);

  try {
    trackAnalyticsLog({
      key: 'deposit.order_status',
      label: '充值订单状态切换',
      value: withDepositFunnelValue({
        rail: 'crypto',
        orderId,
        coboOrderStatus: status,
        coboStatusText: extra.coboStatusText || item?.coboStatusText || undefined,
        chain: extra.chain || item?.chain || item?.chainId || undefined,
        itemId: item?.id || undefined,
        source: item?.source || undefined,
        via: extra.via || 'cobo_order_poll',
      }),
    });
  } catch (err) {
    console.warn('[deposit-status] order_status analytics failed', err);
  }
}

/**
 * 充值弹窗内记住当前所选链（关窗兜底 select_network）。
 * @param {{ chain?: unknown, asset?: unknown }} [input]
 */
export function noteDepositDialogSelectedNetwork(input = {}) {
  const chain = String(input.chain ?? '').trim();
  if (!chain) return;
  const asset = String(input.asset ?? '').trim();
  dialogSelectedNetwork = { chain, asset: asset || undefined };
  // 选链 / 地址展示时即开计时（不依赖开窗 watch 先后顺序）
  armDepositChainDwell({ chain, asset: asset || undefined });
}

/** 关充值弹窗：若尚未因状态卡报过，则上报当前所选链 */
export function flushDepositSelectNetworkOnDialogClose() {
  if (dialogSelectNetworkReported) return;
  if (!dialogSelectedNetwork?.chain) return;
  trackDepositSelectNetworkOnce(
    {
      chain: dialogSelectedNetwork.chain,
      chainId: dialogSelectedNetwork.chain,
      tokenSymbol: dialogSelectedNetwork.asset,
      source: 'dialog',
    },
    {
      via: 'dialog_close',
      phase: 'dialog_close',
      chain: dialogSelectedNetwork.chain,
      asset: dialogSelectedNetwork.asset,
    }
  );
}

/**
 * 仅终态打 success/fail；同一 item/order 只打一次。
 * @param {{ id?: string, coboOrderId?: string, amount?: unknown, tokenSymbol?: string, source?: string, txHash?: string, chainId?: unknown, chain?: unknown }} item
 * @param {'success'|'fail'} kind
 * @param {Record<string, unknown>} [extra]
 */
function trackDepositTerminalOnce(item, kind, extra = {}) {
  const itemId = String(item?.id ?? '').trim();
  const orderId = String(item?.coboOrderId ?? extra.orderId ?? '').trim();
  const keys = [
    itemId ? `${itemId}:${kind}` : '',
    orderId ? `order:${orderId}:${kind}` : '',
  ].filter(Boolean);
  if (!keys.length) return;
  if (keys.some((k) => depositTerminalAnalyticsKeys.has(k))) return;
  keys.forEach((k) => depositTerminalAnalyticsKeys.add(k));

  try {
    if (kind === 'success') {
      const successValue = withDepositFunnelValue({
        amount: item?.amount,
        token: item?.tokenSymbol || 'USDC',
        method: item?.source === 'connected' || item?.source === 'cobo' ? 'crypto' : item?.source,
        rail: 'crypto',
        source: item?.source || undefined,
        txHash: item?.txHash || undefined,
        phase: 'confirmed',
        itemId: itemId || undefined,
        orderId: orderId || undefined,
        chainId: item?.chainId ?? extra.chainId,
        ...extra,
      });
      trackAnalyticsLog({
        key: 'deposit.success',
        label: '充值成功',
        value: successValue,
      });
      afterDepositSuccessTracked(successValue);
      markDepositBonusSuccessful({ amount: item?.amount });
      endAnalyticsFunnelSession('deposit');
      return;
    }
    trackAnalyticsLog({
      key: 'deposit.fail',
      label: '充值失败',
      value: withDepositFunnelValue({
        rail: 'crypto',
        source: item?.source || undefined,
        txHash: item?.txHash || undefined,
        itemId: itemId || undefined,
        orderId: orderId || undefined,
        ...extra,
      }),
    });
    afterDepositFailTracked();
  } catch (err) {
    console.warn('[deposit-status] terminal analytics failed', err);
  }
}

function stopCoboAnalyticsFollowup(orderId) {
  const id = String(orderId ?? '').trim();
  if (!id) return;
  const entry = coboAnalyticsFollowups.get(id);
  if (!entry) return;
  if (entry.timer != null) clearInterval(entry.timer);
  coboAnalyticsFollowups.delete(id);
}

function readCoboSuccessTrackedSet() {
  if (typeof localStorage === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(COBO_SUCCESS_BACKFILL_STORAGE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return new Set(
      (Array.isArray(list) ? list : [])
        .map((id) => String(id ?? '').trim())
        .filter(Boolean)
        .slice(-200)
    );
  } catch {
    return new Set();
  }
}

function markCoboSuccessOrderTracked(orderId) {
  const id = String(orderId ?? '').trim();
  if (!id) return;
  const set = readCoboSuccessTrackedSet();
  if (set.has(id)) return;
  set.add(id);
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(COBO_SUCCESS_BACKFILL_STORAGE_KEY, JSON.stringify([...set].slice(-200)));
  } catch {
    /* ignore */
  }
}

function hasCoboSuccessOrderTracked(orderId) {
  const id = String(orderId ?? '').trim();
  if (!id) return false;
  if (depositTerminalAnalyticsKeys.has(`order:${id}:success`)) return true;
  return readCoboSuccessTrackedSet().has(id);
}

/**
 * 登录后补扫近 24h 已完成 Cobo 单：补 transfer / chain_confirmed / success（按 orderId 去重）。
 */
async function backfillRecentCoboDepositAnalytics() {
  if (!hasAuthSession()) return;
  try {
    const res = await coboDepositOrders({ pageNum: 1, pageSize: 20 });
    const { rows } = normalizeCoboOrdersPage(res);
    const now = Date.now();
    for (const order of rows) {
      const status = resolveCoboOrderStatus(order);
      if (!isCoboOrderCompleted(status)) continue;
      const orderId = extractCoboOrderId(order);
      if (!orderId || hasCoboSuccessOrderTracked(orderId)) continue;
      const created = orderCreatedMs(order);
      if (created > 0 && now - created > COBO_SUCCESS_BACKFILL_MAX_AGE_MS) continue;

      const txHash = extractCoboDepositTxid(order) || '';
      const snapshot = {
        id: `backfill:${orderId}`,
        coboOrderId: orderId,
        amount: extractCoboDisplayAmount(order),
        tokenSymbol: extractCoboDepositCurrency(order) || 'USDC',
        source: 'cobo',
        txHash,
      };
      if (txHash) {
        trackDepositTransferSubmittedOnce(snapshot, { via: 'login_backfill', txHash });
        trackDepositChainConfirmedOnce(snapshot, { via: 'login_backfill', txHash });
      }
      trackDepositTerminalOnce(snapshot, 'success', { via: 'login_backfill' });
      markCoboSuccessOrderTracked(orderId);
    }
  } catch (err) {
    console.warn('[deposit-status] cobo success backfill failed', err);
  }
}

/**
 * 关窗后订单轮询会停：对进行中的 Cobo 单做短时跟单，终态再打埋点。
 * @param {{ id?: string, coboOrderId?: string, amount?: unknown, tokenSymbol?: string, source?: string, txHash?: string }} item
 */
function startCoboAnalyticsFollowup(item) {
  const orderId = String(item?.coboOrderId ?? '').trim();
  if (!orderId) return;
  if (coboAnalyticsFollowups.has(orderId)) return;
  if (depositTerminalAnalyticsKeys.has(`order:${orderId}:success`)) return;
  if (depositTerminalAnalyticsKeys.has(`order:${orderId}:fail`)) return;

  const snapshot = {
    id: item.id,
    coboOrderId: orderId,
    amount: item.amount,
    tokenSymbol: item.tokenSymbol,
    source: item.source || 'cobo',
    txHash: item.txHash,
  };
  const startedAt = Date.now();
  const tick = async () => {
    if (!hasAuthSession()) {
      stopCoboAnalyticsFollowup(orderId);
      return;
    }
    if (Date.now() - startedAt >= COBO_ANALYTICS_FOLLOWUP_MAX_MS) {
      stopCoboAnalyticsFollowup(orderId);
      return;
    }
    try {
      const res = await coboDepositOrders({ pageNum: 1, pageSize: 10 });
      const { rows } = normalizeCoboOrdersPage(res);
      const order = rows.find((row) => extractCoboOrderId(row) === orderId) || null;
      if (!order) return;
      const status = resolveCoboOrderStatus(order);
      if (isCoboOrderCompleted(status)) {
        const txHash = extractCoboDepositTxid(order) || snapshot.txHash;
        const merged = {
          ...snapshot,
          amount: extractCoboDisplayAmount(order) ?? snapshot.amount,
          txHash,
        };
        if (txHash) {
          if (merged.source !== 'connected') {
            trackDepositTransferSubmittedOnce(merged, {
              via: 'followup',
              txHash,
            });
          }
          trackDepositChainConfirmedOnce(merged, { via: 'followup', txHash });
        }
        trackDepositTerminalOnce(merged, 'success', { followup: true });
        markCoboSuccessOrderTracked(orderId);
        stopCoboAnalyticsFollowup(orderId);
        return;
      }
      if (isCoboOrderFailed(status)) {
        trackDepositTerminalOnce(snapshot, 'fail', {
          reason: 'cobo_order_failed',
          followup: true,
        });
        stopCoboAnalyticsFollowup(orderId);
      }
    } catch (err) {
      console.warn('[deposit-status] cobo analytics followup failed', err);
    }
  };
  const timer = setInterval(() => {
    void tick();
  }, COBO_ORDER_POLL_MS);
  coboAnalyticsFollowups.set(orderId, { timer, startedAt });
  void tick();
}

function applyIncomingPollPhase(nextPhase, { kick = false } = {}) {
  if (!incomingSetPhase) return;
  incomingSetPhase(nextPhase, { kick });
  const session = loadDepositWatchSession();
  if (session?.mode === 'incoming') {
    persistIncomingSession(session, { pollPhase: nextPhase });
  }
}

/** 按扫码紧急态 / 关窗冲刺预算 / 是否仍有进行中笔，收敛 poll 频率 */
function reconcileIncomingPollPhase({ kick = false } = {}) {
  // RPC 与 Cobo orders 独立：此处只调链上 getLogs 频率，绝不因 Cobo 停 RPC
  if (!incomingSetPhase) return;

  if (depositWatchUrgent) {
    applyIncomingPollPhase(POLL_PHASE.FAST, { kick });
    return;
  }

  if (hasInProgressItems(depositItems.value)) {
    if (postDialogFastTicksLeft > 0) {
      applyIncomingPollPhase(POLL_PHASE.FAST, { kick });
    } else {
      applyIncomingPollPhase(POLL_PHASE.SLOW, { kick: false });
    }
    return;
  }

  postDialogFastTicksLeft = 0;
  applyIncomingPollPhase(POLL_PHASE.SLOW, { kick: false });
}

function onIncomingMonitorTick() {
  if (depositWatchUrgent) return;
  if (postDialogFastTicksLeft <= 0) return;
  postDialogFastTicksLeft -= 1;
  if (postDialogFastTicksLeft <= 0) {
    reconcileIncomingPollPhase();
  }
}

/** 进入扫码面时刷新 Cobo 订单监听时钟，并把更早的历史充值记入 seen */
async function bumpCoboWatchSessionForNewSurface() {
  if (!coboOrderWatch) return;
  // 已有进行中状态时不要清时钟/灌 seen，避免中间态丢失
  if (hasCoboInProgressItems()) return;
  const watchStartedAt = Date.now() - 60_000;
  let seenTx = Array.isArray(coboOrderWatch.seenTx) ? coboOrderWatch.seenTx : [];
  let seenCoboOrderIds = Array.isArray(coboOrderWatch.seenCoboOrderIds)
    ? coboOrderWatch.seenCoboOrderIds
    : [];
  try {
    const res = await coboDepositOrders({ pageNum: 1, pageSize: 10 });
    const { rows } = normalizeCoboOrdersPage(res);
    const addressList = normalizeCoboAddressList([
      ...(Array.isArray(coboOrderWatch.watchAddresses) ? coboOrderWatch.watchAddresses : []),
      coboOrderWatch.watchAddress,
    ]);
    const historical = collectHistoricalCoboDepositTxids(rows, addressList, {
      notBeforeMs: watchStartedAt,
    });
    const historicalIds = collectHistoricalCoboOrderIds(rows, addressList, {
      notBeforeMs: watchStartedAt,
    });
    seenTx = mergeSeenTxLists(seenTx, historical);
    seenCoboOrderIds = [...new Set([...seenCoboOrderIds, ...historicalIds])];
  } catch (err) {
    console.warn('[deposit-status] bump watch clock seed failed', err);
  }
  patchCoboOrderWatch({ watchStartedAt, seenTx, seenCoboOrderIds });
}

/** 扫码转账面焦点：直转希望快扫；Cobo 只踢订单轮询，不碰 RPC */
export function setDepositWatchUrgent(urgent) {
  const next = Boolean(urgent);
  const rising = next && !depositWatchUrgent;
  const coboListening = Boolean(coboOrderWatch);

  if (next === depositWatchUrgent) {
    if (next) {
      if (coboListening) {
        kickCoboOrderPoller();
        return;
      }
      reconcileIncomingPollPhase({ kick: true });
    }
    return;
  }
  depositWatchUrgent = next;
  if (next) {
    postDialogFastTicksLeft = 0;
    if (rising && coboListening) void bumpCoboWatchSessionForNewSurface();
    kickCoboOrderPoller();
    if (coboListening) return;
    reconcileIncomingPollPhase({ kick: true });
    return;
  }
  if (hasInProgressItems(depositItems.value)) {
    postDialogFastTicksLeft = POST_DIALOG_FAST_MAX_TICKS;
  } else {
    postDialogFastTicksLeft = 0;
  }
  if (coboListening) return;
  reconcileIncomingPollPhase();
}

function getFocusItem() {
  const id = focusItemId.value;
  if (!id) return null;
  return depositItems.value.find((item) => item.id === id) ?? null;
}

function getItemById(id) {
  return depositItems.value.find((item) => item.id === id) ?? null;
}

function getItemByTxHash(hash) {
  const normalized = normalizeTxHashKey(hash);
  if (!normalized) return null;
  return depositItems.value.find((item) => normalizeTxHashKey(item.txHash) === normalized) ?? null;
}

function patchItem(id, patch) {
  const index = depositItems.value.findIndex((item) => item.id === id);
  if (index < 0) return null;
  const next = {
    ...depositItems.value[index],
    ...patch,
    updatedAt: Date.now(),
  };
  if (patch.amount != null) {
    next.amountLabel = formatDepositAmountLabel(patch.amount);
  }
  depositItems.value = [
    ...depositItems.value.slice(0, index),
    next,
    ...depositItems.value.slice(index + 1),
  ];
  saveDepositItemsSnapshot(depositItems.value);
  return next;
}

/** 只跟踪最新一笔；同 tx 则合并更新，否则替换整队（旧笔 tx 写入 seen，防止再弹） */
function addItem(item) {
  const hash = normalizeTxHashKey(item?.txHash);
  const incomingPhase = item?.phase;
  let prevPhase = '';
  if (hash) {
    const existing = getItemByTxHash(hash);
    if (existing) {
      prevPhase = existing.phase;
      const merged = {
        ...existing,
        ...item,
        id: existing.id,
        txHash: hash,
        updatedAt: Date.now(),
      };
      depositItems.value = [merged];
      focusItemId.value = merged.id;
      saveDepositItemsSnapshot(depositItems.value);
      if (incomingPhase === ITEM_PHASE.SUCCESS && prevPhase !== ITEM_PHASE.SUCCESS) {
        try {
          completeHandler?.();
        } catch (err) {
          console.warn('[deposit-status] complete handler failed', err);
        }
        trackDepositTerminalOnce(merged, 'success', { via: 'addItem' });
      } else if (incomingPhase === ITEM_PHASE.FAILED && prevPhase !== ITEM_PHASE.FAILED) {
        trackDepositTerminalOnce(merged, 'fail', { via: 'addItem', reason: 'phase_failed' });
      }
      return merged;
    }
  }

  for (const old of depositItems.value) {
    stopReceiptMonitor(old.id);
    const oldHash = normalizeTxHashKey(old.txHash);
    if (oldHash && oldHash !== hash) {
      appendTxToIncomingSeenTx(oldHash);
    }
  }
  clearPinnedFocus();
  manualFocusItemId = null;
  depositItems.value = [item];
  focusItemId.value = item.id;
  saveDepositItemsSnapshot(depositItems.value);
  if (incomingPhase === ITEM_PHASE.SUCCESS) {
    try {
      completeHandler?.();
    } catch (err) {
      console.warn('[deposit-status] complete handler failed', err);
    }
    trackDepositTerminalOnce(item, 'success', { via: 'addItem' });
  } else if (incomingPhase === ITEM_PHASE.FAILED) {
    trackDepositTerminalOnce(item, 'fail', { via: 'addItem', reason: 'phase_failed' });
  }
  return item;
}

function buildItemFromCoboOrder(order, session, coboAddr) {
  const status = resolveCoboOrderStatus(order);
  const hash = extractCoboDepositTxid(order);
  const orderId = extractCoboOrderId(order);
  const amount = extractCoboDisplayAmount(order);
  const orderCurrency = extractCoboDepositCurrency(order);
  const sessionCurrency = normalizeDepositTokenSymbol(session.tokenSymbol);
  const depositChain = extractCoboDepositChain(order);
  const explorerFromChain = resolveCoboDepositExplorerUrl(depositChain);
  const depositConfirmedAt = extractCoboDepositConfirmedAtMs(order) || null;
  const completedAt = isCoboOrderCompleted(status)
    ? extractCoboCompletedAtMs(order) || Date.now()
    : null;
  const privyAddress =
    extractCoboPrivyAddress(order) || String(session.privyAddress ?? '').trim() || '';

  return buildDepositItem({
    phase: isCoboOrderFailed(status)
      ? ITEM_PHASE.FAILED
      : isCoboOrderCompleted(status)
      ? ITEM_PHASE.SUCCESS
      : ITEM_PHASE.DEPOSITING,
    txHash: hash,
    amount: amount,
    tokenSymbol: orderCurrency || sessionCurrency || 'USDC',
    watchAddress: coboAddr,
    fromAddress: String(order.fromAddress ?? '').trim(),
    chainId: session.chainId,
    explorerUrl: explorerFromChain || session.explorerUrl || '',
    completionExplorerUrl:
      session.completionExplorerUrl || resolveDepositExplorerUrl(resolveDefaultChainId()),
    startedWithDialogOpen: depositDialogOpen.value,
    uiSurface: resolveUiSurface(),
    source: 'cobo',
    depositRail: 'cobo',
    coboAddress: coboAddr || extractCoboDepositAddress(order),
    privyAddress,
    coboOrderId: orderId,
    coboOrderStatus: Number.isFinite(status) ? status : null,
    coboStatusText: String(order.statusText ?? '').trim(),
    exchangeStatus: order.exchangeStatus,
    completionTxHash: extractCoboCompletionTxid(order),
    coboOnChainConfirmed: Boolean(hash),
    detectedAt: Date.now(),
    depositConfirmedAt,
    confirmedAt: completedAt,
    coboOrderCreatedAt: orderCreatedMs(order) || Date.now(),
  });
}

function replaceDepositItems(items) {
  const list = dedupeDepositItemsByTxHash(items);
  if (list.length <= 1) {
    depositItems.value = list;
  } else {
    // 只留创建/入账时间最新的一笔（不用 updatedAt，避免轮询刷新导致来回切）
    list.sort((a, b) => {
      const at = Number(a.coboOrderCreatedAt || a.detectedAt || a.createdAt) || 0;
      const bt = Number(b.coboOrderCreatedAt || b.detectedAt || b.createdAt) || 0;
      return bt - at;
    });
    depositItems.value = list.slice(0, 1);
  }
  saveDepositItemsSnapshot(depositItems.value);
}

function setFocus(id) {
  focusItemId.value = id;
}

/** 多笔到账不自动抢焦点：仅无当前展示项时才切到新笔 */
function setFocusIfIdle(id) {
  if (!id) return;
  const current = getFocusItem();
  if (!current) {
    focusItemId.value = id;
    return;
  }
  const stillShowing =
    isItemInProgress(current.phase) ||
    (isItemTerminal(current.phase) && (!current.navDismissed || !current.embeddedSeen));
  if (stillShowing) return;
  focusItemId.value = id;
}

function pinFocus(id) {
  manualFocusItemId = null;
  pinnedFocusItemId = id;
  focusItemId.value = id;
}

function clearPinnedFocus(id) {
  if (!id || pinnedFocusItemId === id) {
    pinnedFocusItemId = null;
  }
}

function isItemStillShowable(item) {
  if (!item) return false;
  // 用户点叉（双表面都 ack）后同一笔不再作为可展示焦点
  if (item.navDismissed && item.embeddedSeen) return false;
  return isItemInProgress(item.phase) || isItemTerminal(item.phase);
}

/** 归档已结束的旧条目，避免再次充值/开弹窗时闪「充值成功」 */
function archiveStaleTerminalItems(exceptId = null) {
  let changed = false;
  const next = depositItems.value.map((item) => {
    if (item.id === exceptId) return item;
    if (!isItemTerminal(item.phase)) return item;
    if (item.embeddedSeen && item.navDismissed) return item;
    changed = true;
    return {
      ...item,
      navDisplayed: true,
      navDismissed: true,
      embeddedSeen: true,
      updatedAt: Date.now(),
    };
  });
  if (changed) {
    replaceDepositItems(next);
  }
}

function appendTxToIncomingSeenTx(txHash) {
  const hash = normalizeTxHashKey(txHash);
  if (!hash) return;
  const session = loadDepositWatchSession();
  if (session?.mode === 'incoming') {
    const seen = mergeSeenTxLists(session.seenTx, [hash]);
    if (seen.length !== (session.seenTx?.length ?? 0)) {
      persistIncomingSession(session, { seenTx: seen });
    }
  }
  // 同步写入正在跑的监听 Set，否则仅改 session 拦不住本轮 getLogs
  incomingMarkSeen?.(hash);
}

function resolveFocusItemId() {
  if (manualFocusItemId) {
    const manual = getItemById(manualFocusItemId);
    if (isItemStillShowable(manual)) {
      return manualFocusItemId;
    }
    manualFocusItemId = null;
  }

  const pinned = pinnedFocusItemId ? getItemById(pinnedFocusItemId) : null;
  if (pinned && isItemStillShowable(pinned)) {
    return pinned.id;
  }
  if (pinned) {
    pinnedFocusItemId = null;
  }

  // 保持当前展示笔，多笔到账时不自动跳到最新
  const current = focusItemId.value ? getItemById(focusItemId.value) : null;
  if (isItemStillShowable(current)) {
    return current.id;
  }

  return pickFocusItemId(depositItems.value) ?? focusItemId.value;
}

function resolveUiSurface() {
  return depositDialogOpen.value ? 'embedded' : 'nav';
}

function clearNavAutoCloseTimer() {
  if (navAutoCloseTimer != null) {
    clearTimeout(navAutoCloseTimer);
    navAutoCloseTimer = null;
  }
}

function markItemAcknowledged(item) {
  if (!item) return;
  patchItem(item.id, {
    navDisplayed: true,
    navDismissed: true,
    embeddedSeen: true,
  });
  // 点叉后写入 seen，避免同笔再被链上检出并二次弹窗
  if (item.txHash) appendTxToIncomingSeenTx(item.txHash);
}

function dismissNavDepositStatus() {
  clearNavAutoCloseTimer();
  const focus = getFocusItem();
  const nav = pickNavItem(depositItems.value, focus);
  if (nav) {
    markItemAcknowledged(nav);
    clearPinnedFocus(nav.id);
  }
  syncDepositUiVisibility();
}

function scheduleNavAutoCloseIfNeeded() {
  clearNavAutoCloseTimer();
  if (!navVisible.value) return;
  const item = navItem.value;
  if (!item) return;
  const p = item.phase;
  if (p !== ITEM_PHASE.SUCCESS && p !== ITEM_PHASE.FAILED) return;

  navAutoCloseTimer = setTimeout(() => {
    navAutoCloseTimer = null;
    dismissNavDepositStatus();
  }, NAV_TOAST_AUTO_CLOSE_MS);
}

function syncDepositUiVisibility() {
  if (!hasAuthSession()) {
    clearNavAutoCloseTimer();
    navVisible.value = false;
    embeddedVisible.value = false;
    return;
  }

  focusItemId.value = resolveFocusItemId();

  const focus = getFocusItem();
  const dialogOpen = depositDialogOpen.value;
  const items = depositItems.value;

  if (!focus) {
    clearNavAutoCloseTimer();
    navVisible.value = false;
    embeddedVisible.value = false;
    return;
  }

  const showEmbedded = shouldShowEmbeddedForItem(focus, dialogOpen, items);
  const navTarget = pickNavItem(items, focus);
  const showNav = navTarget ? shouldShowNavForItem(navTarget, dialogOpen) : false;

  const wasEmbedded = embeddedVisible.value;
  // 关窗后不弹右上角确认卡（仅充值弹窗内 Embedded）
  navVisible.value = false;
  embeddedVisible.value = showEmbedded;

  // 入账状态卡首次出现 → 上报所用链（非后台 success）
  if (showEmbedded && !wasEmbedded && focus) {
    trackDepositSelectNetworkOnce(focus, {
      via: 'status_ui',
      phase: 'status_ui',
      chain: focus.chain || focus.chainId,
    });
  }

  if (showNav && navTarget && !navTarget.navDisplayed) {
    patchItem(navTarget.id, { navDisplayed: true });
  }
  if (showEmbedded && focus && !focus.navDisplayed) {
    patchItem(focus.id, { navDisplayed: true });
  }

  scheduleNavAutoCloseIfNeeded();
}

function hideAllDepositUi() {
  clearNavAutoCloseTimer();
  pinnedFocusItemId = null;
  manualFocusItemId = null;
  navVisible.value = false;
  embeddedVisible.value = false;
  depositItems.value = [];
  focusItemId.value = null;
  saveDepositItemsSnapshot([]);
}

function stopReceiptMonitor(itemId) {
  const stop = receiptStops.get(itemId);
  if (stop) {
    stop();
    receiptStops.delete(itemId);
  }
}

function stopAllReceiptMonitors() {
  for (const stop of receiptStops.values()) {
    stop();
  }
  receiptStops.clear();
}

function stopIncomingMonitor() {
  incomingStop?.();
  incomingStop = null;
  incomingGetState = null;
  incomingSetPhase = null;
  incomingMarkSeen = null;
}

function applyItemPhase(itemId, nextPhase, extra = {}) {
  if (!hasAuthSession()) return;

  const current = getItemById(itemId);
  if (!current) return;

  // 终态不可回退为进行中（防止扫链重放造成 UI 与资金状态错乱）
  if (isItemTerminal(current.phase) && isItemInProgress(nextPhase)) {
    return;
  }

  // 已是同一终态：仅合并字段，不重复弹窗/回调
  if (current.phase === nextPhase && isItemTerminal(nextPhase)) {
    const hasNewFields = Object.entries(extra).some(([key, val]) => {
      if (val == null || val === '') return false;
      return String(current[key] ?? '') !== String(val);
    });
    if (hasNewFields) {
      patchItem(itemId, extra);
    }
    return;
  }

  const transitioningToSuccess =
    nextPhase === ITEM_PHASE.SUCCESS && current.phase !== ITEM_PHASE.SUCCESS;
  const transitioningToFail =
    nextPhase === ITEM_PHASE.FAILED && current.phase !== ITEM_PHASE.FAILED;

  const phaseExtra = { ...extra };
  if (nextPhase === ITEM_PHASE.DEPOSITING && !current.detectedAt && phaseExtra.detectedAt == null) {
    phaseExtra.detectedAt = Date.now();
  }
  if (nextPhase === ITEM_PHASE.SUCCESS && !current.confirmedAt && phaseExtra.confirmedAt == null) {
    phaseExtra.confirmedAt = Date.now();
  }

  const item = patchItem(itemId, { phase: nextPhase, ...phaseExtra });
  if (!item) return;

  const hash = String(item.txHash ?? extra.txHash ?? '').trim();
  if (hash && isItemTerminal(nextPhase)) {
    appendTxToIncomingSeenTx(hash);
  }

  if (transitioningToSuccess) {
    try {
      completeHandler?.();
    } catch (err) {
      console.warn('[deposit-status] complete handler failed', err);
    }
    // Cobo：若订单路径未打过 chain_confirmed，成功前补一次
    if (isCoboDepositItem(item) && String(item.txHash ?? '').trim()) {
      trackDepositChainConfirmedOnce(item, { via: 'success_backfill' });
    }
    if (!isCoboDepositItem(item)) {
      trackDepositChainConfirmedOnce(item, { via: 'receipt_success' });
    }
    trackDepositTerminalOnce(item, 'success');
    if (item.coboOrderId) markCoboSuccessOrderTracked(item.coboOrderId);
    stopCoboAnalyticsFollowup(item.coboOrderId);
  } else if (transitioningToFail) {
    trackDepositTerminalOnce(item, 'fail', { reason: 'phase_failed' });
    stopCoboAnalyticsFollowup(item.coboOrderId);
  }

  if (isItemTerminal(nextPhase)) {
    stopReceiptMonitor(itemId);
    if (!hasInProgressItems(depositItems.value)) {
      postDialogFastTicksLeft = 0;
    }
    reconcileIncomingPollPhase();
  }

  syncDepositUiVisibility();
}

function markCoboOnChainConfirmed(itemId, extra = {}) {
  const item = getItemById(itemId);
  if (!item || isItemTerminal(item.phase)) return;
  const { blockTimestampMs, ...rest } = extra;
  const patch = {
    coboOnChainConfirmed: true,
    ...rest,
  };
  const chainMs = Number(blockTimestampMs);
  if (Number.isFinite(chainMs) && chainMs > 0) {
    patch.depositConfirmedAt = item.depositConfirmedAt || chainMs;
  }
  const wasConfirmed = Boolean(item.coboOnChainConfirmed);
  const next = patchItem(itemId, patch);
  if (!wasConfirmed && next) {
    // Connected 已在弹窗打过 transfer_submitted；此处只补手动入金缺失的提交事件
    if (next.source !== 'connected') {
      trackDepositTransferSubmittedOnce(next, {
        via: 'watcher',
        txHash: next.txHash || rest.txHash,
      });
    }
    trackDepositChainConfirmedOnce(next, {
      via: 'cobo_on_chain',
      txHash: next.txHash || rest.txHash,
    });
  }
  ensureCoboOrderPoller();
  syncDepositUiVisibility();
  if (!patch.depositConfirmedAt && !item.depositConfirmedAt) {
    void hydrateItemChainTimes(itemId);
  }
}

/** 异步补齐充值交易 / 到账交易的链上出块时间 */
async function hydrateItemChainTimes(itemId) {
  if (FUNDING_V2_ENABLED) return;
  const item = getItemById(itemId);
  if (!item || !isDepositWatchAllowed()) return;
  // Cobo：时间戳来自订单字段，不查 RPC
  if (isCoboDepositItem(item)) return;

  const patch = {};
  if (!item.depositConfirmedAt && item.txHash) {
    try {
      const ms = await resolveTxBlockTimeMs(resolveDepositRpcUrl(item.chainId), item.txHash);
      if (ms) patch.depositConfirmedAt = ms;
    } catch (err) {
      console.warn('[deposit-status] hydrate depositConfirmedAt failed', err);
    }
  }

  const completionHash = looksLikeTxHash(item.completionTxHash) ? item.completionTxHash : '';
  if (completionHash) {
    try {
      const ms = await resolveTxBlockTimeMs(
        resolveDepositRpcUrl(resolveDefaultChainId()),
        completionHash
      );
      // 到账时间优先用完成交易出块时间
      if (ms && (!item.confirmedAt || item.phase === ITEM_PHASE.SUCCESS)) {
        patch.confirmedAt = ms;
      }
    } catch (err) {
      console.warn('[deposit-status] hydrate confirmedAt failed', err);
    }
  }

  if (!Object.keys(patch).length) return;
  if (!getItemById(itemId)) return;
  patchItem(itemId, patch);
  syncDepositUiVisibility();
}

function hasCoboInProgressItems() {
  return depositItems.value.some((item) => isCoboDepositItem(item) && isItemInProgress(item.phase));
}

/**
 * 弹窗内 Cobo 订单轮询状态（内存态，与 RPC 的 deposit-chain-watch 会话完全分离）
 * @type {null | {
 *   watchKey: string,
 *   watchAddress: string,
 *   watchAddresses: string[],
 *   tokenSymbol: string,
 *   decimals: number,
 *   chainId: *,
 *   explorerUrl: string,
 *   completionExplorerUrl: string,
 *   privyAddress: string,
 *   watchStartedAt: number,
 *   seenCoboOrderIds: string[],
 *   seenTx: string[],
 * }}
 */
let coboOrderWatch = null;
let backendNextPollAt = 0;

function ensureBackendDepositWatch() {
  if (!hasAuthSession()) return;
  stopIncomingMonitor();
  stopAllReceiptMonitors();
  clearDepositWatchSession();
  if (!coboOrderWatch) {
    coboOrderWatch = {
      watchKey: 'backend-funding',
      watchAddress: '',
      watchAddresses: [],
      tokenSymbol: 'USDC',
      watchStartedAt: Date.now() - 60_000,
      seenCoboOrderIds: loadCoboOrderSuppress().seenOrderIds || [],
      seenTx: [],
    };
  }
  ensureCoboOrderPoller();
}

/** 新资金模式登录/回前台补启；已有定时器和轮询节奏保持不变。 */
export function ensureBackendDepositWatchAfterLogin() {
  if (!FUNDING_V2_ENABLED) return;
  ensureBackendDepositWatch();
}

function patchCoboOrderWatch(patch = {}) {
  if (!coboOrderWatch) return;
  coboOrderWatch = { ...coboOrderWatch, ...patch };
}

function clearCoboOrderWatch() {
  coboOrderWatch = null;
}

/** 停掉弹窗 orders 轮询（不影响 RPC；抑制态保留在 sessionStorage） */
export function stopCoboDepositOrderWatch() {
  if (FUNDING_V2_ENABLED && hasAuthSession()) return;
  stopCoboOrderPoller();
  clearCoboOrderWatch();
}

export function isCoboDepositOrderWatchActive() {
  return Boolean(coboOrderWatch) && coboOrderPollTimer != null;
}

/** 关窗 / 切法币：写入抑制态并 ack Cobo 状态卡，停 orders 轮询 */
function persistAndAckCoboDepositUi({ bumpNotBefore = true } = {}) {
  const orderIds = [];
  const txHashes = [];
  if (coboOrderWatch) {
    if (Array.isArray(coboOrderWatch.seenCoboOrderIds)) {
      orderIds.push(...coboOrderWatch.seenCoboOrderIds);
    }
    if (Array.isArray(coboOrderWatch.seenTx)) {
      txHashes.push(...coboOrderWatch.seenTx);
    }
  }
  for (const item of depositItems.value) {
    if (!isCoboDepositItem(item)) continue;
    const id = String(item.coboOrderId ?? '').trim();
    if (id) orderIds.push(id);
    const tx = String(item.txHash ?? '').trim();
    if (tx) txHashes.push(tx);
    markItemAcknowledged(item);
  }
  mergeCoboOrderSuppress({
    orderIds,
    txHashes,
    notBeforeMs: bumpNotBefore ? Date.now() : null,
  });
  stopCoboDepositOrderWatch();
  embeddedVisible.value = false;
  focusItemId.value = pickFocusItemId(depositItems.value);
  syncDepositUiVisibility();
}

/** 关窗：抬高时间窗 + 写入已见 orderId，并 ack 队列里的 Cobo 条 */
function flushCoboSuppressOnDialogClose() {
  for (const item of depositItems.value) {
    if (!isCoboDepositItem(item) || !isItemInProgress(item.phase)) continue;
    startCoboAnalyticsFollowup(item);
  }
  persistAndAckCoboDepositUi({ bumpNotBefore: true });
}

/** 切到法币等非 Cobo 面：停 orders、收起上一笔加密状态卡（主弹窗可仍开着） */
export function pauseCoboDepositWatchForFiatSurface() {
  persistAndAckCoboDepositUi({ bumpNotBefore: true });
}

/** 清理历史上误写入 RPC 会话的 depositRail:'cobo' */
function scrubLegacyCoboRailFromRpcSession() {
  const session = loadDepositWatchSession();
  if (!session || session.depositRail !== 'cobo') return false;
  clearDepositWatchSession();
  if (String(activeWatchKey).startsWith('cobo-order:')) {
    activeWatchKey = '';
  }
  return true;
}

function stopCoboOrderPoller() {
  if (coboOrderPollTimer != null) {
    clearInterval(coboOrderPollTimer);
    coboOrderPollTimer = null;
  }
}

function ensureCoboOrderPoller() {
  if (!hasAuthSession()) return;
  // 仅充值弹窗打开时轮询订单
  if (!FUNDING_V2_ENABLED && !depositDialogOpen.value) return;
  if (!coboOrderWatch) return;
  if (coboOrderPollTimer != null) return;
  coboOrderPollTimer = setInterval(() => {
    void tickCoboDepositOrders();
  }, COBO_ORDER_POLL_MS);
  void tickCoboDepositOrders();
}

/** 扫码面打开时立刻拉一次订单，避免只靠 3s 间隔错过 status 1–4 */
function kickCoboOrderPoller() {
  ensureCoboOrderPoller();
  void tickCoboDepositOrders();
}

function applyCoboOrderToItem(itemId, order) {
  if (!order || !hasAuthSession()) return;
  const item = getItemById(itemId);
  if (!item || !isCoboDepositItem(item)) return;
  if (isItemTerminal(item.phase)) return;

  const status = resolveCoboOrderStatus(order);
  const prevStatus = Number(item.coboOrderStatus);
  const depositTx = extractCoboDepositTxid(order) || item.txHash;
  const completionTx = extractCoboCompletionTxid(order);
  const orderId = extractCoboOrderId(order);
  const amount = extractCoboDisplayAmount(order);
  const orderCurrency = extractCoboDepositCurrency(order);
  const depositChain = extractCoboDepositChain(order);
  const explorerFromChain = resolveCoboDepositExplorerUrl(depositChain);
  const depositConfirmedAt =
    extractCoboDepositConfirmedAtMs(order) || item.depositConfirmedAt || null;
  const privyAddress = extractCoboPrivyAddress(order) || item.privyAddress || '';

  // orders 扫描：状态码切换即上报（含首次拿到 status）
  if (Number.isFinite(status) && (!Number.isFinite(prevStatus) || prevStatus !== status)) {
    trackDepositOrderStatusOnce(
      {
        ...item,
        coboOrderId: orderId || item.coboOrderId,
        chain: depositChain || item.chain,
        chainId: item.chainId,
      },
      status,
      {
        orderId: orderId || item.coboOrderId,
        coboStatusText: String(order.statusText ?? item.coboStatusText ?? '').trim(),
        chain: depositChain || undefined,
        via: 'cobo_order_poll',
      }
    );
  }

  const patch = {
    coboOrderId: orderId || item.coboOrderId,
    coboOrderStatus: Number.isFinite(status) ? status : item.coboOrderStatus,
    coboStatusText: String(order.statusText ?? item.coboStatusText ?? '').trim(),
    exchangeStatus: order.exchangeStatus != null ? order.exchangeStatus : item.exchangeStatus,
    txHash: depositTx || item.txHash,
    completionTxHash: completionTx,
    fromAddress: String(order.fromAddress ?? item.fromAddress ?? '').trim(),
    coboAddress: String(
      extractCoboDepositAddress(order) || item.coboAddress || item.watchAddress
    ).trim(),
    privyAddress,
    coboOnChainConfirmed: Boolean(depositTx) || item.coboOnChainConfirmed,
    detectedAt: item.detectedAt ?? Date.now(),
  };
  if (depositChain) {
    patch.chain = depositChain;
  }
  if (explorerFromChain) {
    patch.explorerUrl = explorerFromChain;
  }
  if (depositConfirmedAt) {
    patch.depositConfirmedAt = depositConfirmedAt;
  }
  // Cobo 订单币种以接口为准（链上实际入账）；本地会话可能因默认选中 USDT 而标错
  if (orderCurrency) {
    patch.tokenSymbol = orderCurrency;
  } else if (!normalizeDepositTokenSymbol(item.tokenSymbol)) {
    patch.tokenSymbol = 'USDC';
  }
  if (Number.isFinite(amount) && amount > 0) {
    patch.amount = amount;
  }
  const createdMs = orderCreatedMs(order);
  if (createdMs > 0) {
    patch.coboOrderCreatedAt = item.coboOrderCreatedAt || createdMs;
  }

  if (isCoboOrderFailed(status)) {
    applyItemPhase(itemId, ITEM_PHASE.FAILED, patch);
    appendSeenCoboOrderId(orderId);
    stopCoboOrderPollerIfIdle();
    return;
  }

  const firstDepositTx =
    Boolean(String(depositTx ?? '').trim()) &&
    !String(item.txHash ?? '').trim() &&
    !item.coboOnChainConfirmed;

  if (isCoboOrderCompleted(status)) {
    const completedAt = extractCoboCompletedAtMs(order) || item.confirmedAt || Date.now();
    const merged = { ...item, ...patch };
    // 订单直跳完成时补齐中间态，避免只有 success
    if (String(depositTx ?? '').trim()) {
      if (merged.source !== 'connected') {
        trackDepositTransferSubmittedOnce(merged, {
          via: 'cobo_order',
          txHash: depositTx,
        });
      }
      trackDepositChainConfirmedOnce(merged, {
        via: 'cobo_order',
        txHash: depositTx,
      });
    }
    applyItemPhase(itemId, ITEM_PHASE.SUCCESS, {
      ...patch,
      confirmedAt: completedAt,
    });
    appendSeenCoboOrderId(orderId);
    stopCoboOrderPollerIfIdle();
    return;
  }

  patchItem(itemId, patch);
  if (firstDepositTx || (Boolean(depositTx) && !item.coboOnChainConfirmed)) {
    const next = getItemById(itemId) || { ...item, ...patch };
    if (next.source !== 'connected') {
      trackDepositTransferSubmittedOnce(next, {
        via: 'cobo_order',
        txHash: depositTx,
      });
    }
    trackDepositChainConfirmedOnce(next, {
      via: 'cobo_order',
      txHash: depositTx,
    });
  }
  syncDepositUiVisibility();
}

function appendSeenCoboOrderId(orderId) {
  const id = String(orderId ?? '').trim();
  if (!id) return;
  if (coboOrderWatch) {
    const prev = Array.isArray(coboOrderWatch.seenCoboOrderIds)
      ? coboOrderWatch.seenCoboOrderIds
      : [];
    if (!prev.includes(id)) {
      patchCoboOrderWatch({ seenCoboOrderIds: [...prev, id] });
    }
  }
  mergeCoboOrderSuppress({ orderIds: [id] });
}

function stopCoboOrderPollerIfIdle() {
  // 关窗必须停；进行中单也只在弹窗内跟
  if (!FUNDING_V2_ENABLED && !depositDialogOpen.value) {
    stopCoboOrderPoller();
    return;
  }
  if (hasCoboInProgressItems()) return;
  // 弹窗内 Cobo 监听仍在：绝不能停
  if (coboOrderWatch) return;
  if (depositWatchUrgent) return;
  stopCoboOrderPoller();
}

async function tickCoboDepositOrders() {
  if (coboOrderPollInflight || !hasAuthSession()) return;
  if (!FUNDING_V2_ENABLED && !depositDialogOpen.value) {
    stopCoboOrderPoller();
    return;
  }
  const watch = coboOrderWatch;
  if (!watch) {
    stopCoboOrderPoller();
    return;
  }

  if (FUNDING_V2_ENABLED) {
    if (Date.now() < backendNextPollAt) return;
    const hidden = typeof document !== 'undefined' && document.hidden;
    backendNextPollAt =
      Date.now() +
      (hidden ? 60_000 : hasCoboInProgressItems() || depositDialogOpen.value ? 3_000 : 30_000);
  }
  coboOrderPollInflight = true;
  const startedGen = getAuthGeneration();
  try {
    const tracked = depositItems.value.find(
      (item) => isCoboDepositItem(item) && isItemInProgress(item.phase)
    );
    const res = await coboDepositOrders(
      FUNDING_V2_ENABLED
        ? { watch: true, trackedId: tracked?.coboOrderId || '', trackedTx: tracked?.txHash || '' }
        : { pageNum: 1, pageSize: 10 }
    );
    if (!isDepositWatchAllowed(startedGen)) return;
    if (!coboOrderWatch || coboOrderWatch.watchKey !== watch.watchKey) return;
    const { rows } = normalizeCoboOrdersPage(res);
    // 只扫近 5 分钟（再叠加关窗时间窗）；5 分钟内再靠 seen「提示过不弹」
    const notBeforeMs = coboOrderPromptNotBeforeMs(watch.watchStartedAt);
    // 多链：弹窗会话内任一链收款地址命中即可（不限当前二维码链）
    const addressList = normalizeCoboAddressList([
      ...(FUNDING_V2_ENABLED ? rows.map(extractCoboDepositAddress) : []),
      ...(Array.isArray(watch.watchAddresses) ? watch.watchAddresses : []),
      watch.watchAddress,
      depositItems.value.find((item) => isCoboDepositItem(item) && isItemInProgress(item.phase))
        ?.coboAddress,
      depositItems.value.find((item) => isCoboDepositItem(item) && isItemInProgress(item.phase))
        ?.watchAddress,
    ]);
    const seenOrderIds = Array.isArray(watch.seenCoboOrderIds) ? watch.seenCoboOrderIds : [];

    // 整会话只盯「最新进行中订单」；第二笔一旦出现就替换第一笔，避免被旧单锁死
    const newest =
      addressList.length && notBeforeMs > 0
        ? pickNewestInProgressCoboOrder(rows, addressList, {
            notBeforeMs: FUNDING_V2_ENABLED ? 0 : notBeforeMs,
          })
        : null;
    const live = depositItems.value.find(
      (item) => isCoboDepositItem(item) && isItemInProgress(item.phase) && isItemStillShowable(item)
    );

    const resolveOrderCoboAddr = (order) =>
      extractCoboDepositAddress(order) ||
      String(watch.watchAddress ?? '').trim() ||
      addressList[0] ||
      '';

    if (newest) {
      const newestTx = normalizeTxHashKey(extractCoboDepositTxid(newest));
      const newestId = extractCoboOrderId(newest);
      const newestCreated = orderCreatedMs(newest);
      const liveTx = normalizeTxHashKey(live?.txHash);
      const liveId = String(live?.coboOrderId ?? '').trim();
      const liveCreated = Number(
        live?.coboOrderCreatedAt || live?.detectedAt || live?.createdAt || 0
      );
      const sameTx = Boolean(newestTx && liveTx && newestTx === liveTx);
      const sameId = Boolean(newestId && liveId && newestId === liveId);
      const differentOrder =
        (newestId && liveId && newestId !== liveId) || (newestTx && liveTx && newestTx !== liveTx);
      const newestIsNewer = !live || newestCreated >= liveCreated;
      const coboAddr = resolveOrderCoboAddr(newest);

      if (live && (sameTx || sameId)) {
        // 已在跟的同一笔：只回填状态，不重新弹
        applyCoboOrderToItem(live.id, newest);
      } else if (
        live &&
        differentOrder &&
        newestIsNewer &&
        shouldPromptCoboOrder(newest, seenOrderIds)
      ) {
        // 5 分钟内、未提示过的新一笔 → 替换
        const item = buildItemFromCoboOrder(newest, watch, coboAddr);
        addItem(item);
        appendSeenCoboOrderId(newestId);
        syncDepositUiVisibility();
      } else if (live && !differentOrder) {
        applyCoboOrderToItem(live.id, newest);
      } else if (
        !live &&
        (shouldPromptCoboOrder(newest, seenOrderIds) ||
          (FUNDING_V2_ENABLED && !seenOrderIds.includes(newestId)))
      ) {
        // 恢复未完成订单不受新提示时间窗限制；已关闭的提示仍用 seen 防重。
        const item = buildItemFromCoboOrder(newest, watch, coboAddr);
        addItem(item);
        appendSeenCoboOrderId(newestId);
        syncDepositUiVisibility();
      }
      // else：超过 5 分钟 / 已提示过 → 不弹
    } else if (live) {
      // newest 不在进行中列表：按 tx/orderId 回填（含冲向 status=5）
      const order = findCoboOrderForItem(rows, live, { notBeforeMs: 0 });
      if (order) applyCoboOrderToItem(live.id, order);
    } else if (addressList.length && notBeforeMs > 0) {
      // 终态首见：同样 5 分钟 + seen
      const terminal = pickNewestUnseenTerminalCoboOrder(rows, addressList, {
        notBeforeMs,
        seenOrderIds,
      });
      if (terminal && shouldPromptCoboOrder(terminal, seenOrderIds)) {
        const item = buildItemFromCoboOrder(terminal, watch, resolveOrderCoboAddr(terminal));
        addItem(item);
        appendSeenCoboOrderId(extractCoboOrderId(terminal));
        syncDepositUiVisibility();
      }
    }

    // 终态回填（若仍有进行中条）
    for (const item of depositItems.value) {
      if (!isCoboDepositItem(item) || !isItemInProgress(item.phase)) continue;
      const order = findCoboOrderForItem(rows, item, { notBeforeMs: 0 });
      if (order) applyCoboOrderToItem(item.id, order);
    }

    // 防御：永远只保留一笔
    if (depositItems.value.length > 1) {
      replaceDepositItems(depositItems.value);
      focusItemId.value = depositItems.value[0]?.id ?? focusItemId.value;
    }

    if (FUNDING_V2_ENABLED && hasCoboInProgressItems() && !document.hidden) {
      backendNextPollAt = Math.min(backendNextPollAt, Date.now() + 3_000);
    }
    stopCoboOrderPollerIfIdle();
  } catch (err) {
    console.warn('[deposit-status] cobo orders poll failed', err);
  } finally {
    coboOrderPollInflight = false;
  }
}

async function reconcileItemPhaseWithChain(item) {
  if (FUNDING_V2_ENABLED) {
    ensureBackendDepositWatch();
    return item;
  }
  if (isCoboDepositItem(item)) return item;
  const hash = String(item?.txHash ?? '').trim();
  if (!hash || !isItemInProgress(item.phase)) return item;

  const startedGen = getAuthGeneration();
  try {
    const rpcUrl = resolveDepositRpcUrl(item.chainId);
    const receipt = await fetchTransactionReceipt(rpcUrl, hash);
    if (!isDepositWatchAllowed(startedGen)) return item;
    if (!receipt) return item;

    if (isReceiptSuccess(receipt)) {
      const blockTimestampMs = await resolveReceiptBlockTimeMs(rpcUrl, receipt);
      if (!isDepositWatchAllowed(startedGen)) return item;
      applyItemPhase(item.id, ITEM_PHASE.SUCCESS, {
        txHash: hash,
        ...(blockTimestampMs
          ? { depositConfirmedAt: blockTimestampMs, confirmedAt: blockTimestampMs }
          : {}),
      });
      return getItemById(item.id) ?? item;
    }
    if (isReceiptFailed(receipt)) {
      applyItemPhase(item.id, ITEM_PHASE.FAILED, { txHash: hash });
      return getItemById(item.id) ?? item;
    }
  } catch (err) {
    console.warn('[deposit-status] reconcile receipt failed', hash, err);
  }
  return item;
}

function startReceiptMonitorForItem(item) {
  if (FUNDING_V2_ENABLED) {
    ensureBackendDepositWatch();
    return;
  }
  const hash = String(item.txHash ?? '').trim();
  if (!hash || !hasAuthSession()) return;
  // Cobo：进度与成功只认订单接口，不再查链上 receipt
  if (isCoboDepositItem(item)) return;

  if (isItemTerminal(item.phase)) {
    stopReceiptMonitor(item.id);
    return;
  }

  stopReceiptMonitor(item.id);

  const startedGen = getAuthGeneration();
  const rpcUrl = resolveDepositRpcUrl(item.chainId);
  const monitor = pollTxReceipt({
    rpcUrl,
    txHash: hash,
    onConfirmed: ({ blockTimestampMs } = {}) => {
      if (!isDepositWatchAllowed(startedGen)) return;
      applyItemPhase(item.id, ITEM_PHASE.SUCCESS, {
        ...(blockTimestampMs
          ? { depositConfirmedAt: blockTimestampMs, confirmedAt: blockTimestampMs }
          : {}),
      });
      ensureIncomingWatchAfterTx(item);
    },
    onFailed: () => {
      if (!isDepositWatchAllowed(startedGen)) return;
      applyItemPhase(item.id, ITEM_PHASE.FAILED);
      ensureIncomingWatchAfterTx(item);
    },
    onTimeout: () => {
      if (!isDepositWatchAllowed(startedGen)) return;
      applyItemPhase(item.id, ITEM_PHASE.FAILED);
    },
  });

  receiptStops.set(item.id, monitor.stop);
}

function ensureIncomingWatchAfterTx(item) {
  if (!isDepositWatchAllowed()) return;
  // Cobo 单失败后勿拉起 Pol getLogs 给 Cobo 地址
  if (isCoboDepositItem(item)) return;
  const monitorAddress = String(item.fromAddress || item.watchAddress || '').trim();
  const tokenContracts = resolveDepositWatchContracts({
    tokenSymbol: item.tokenSymbol,
  });
  if (monitorAddress && tokenContracts.length) {
    startDepositIncomingWatch({
      watchAddress: monitorAddress,
      tokenContracts,
      tokenSymbol: item.tokenSymbol,
      chainId: item.chainId,
      initialPhase: POLL_PHASE.TAIL,
      persistent: true,
    });
  }
}

function upsertItemFromIncoming({
  amount,
  txHash: hash,
  fromAddress: from,
  session,
  skipSync = false,
}) {
  const normalized = normalizeTxHashKey(hash);
  const current = depositItems.value.find((item) => isItemInProgress(item.phase));
  // Cobo：无 hash / 同 hash → 合并进当前条；异 hash → 用最新一笔替换（勿被第一笔锁死）
  if (current && (session?.depositRail === 'cobo' || isCoboDepositItem(current)) && normalized) {
    const currentTx = normalizeTxHashKey(current.txHash);
    if (!currentTx || currentTx === normalized) {
      patchItem(current.id, {
        phase: isItemTerminal(current.phase) ? current.phase : ITEM_PHASE.DEPOSITING,
        amount: amount ?? current.amount,
        fromAddress: from || current.fromAddress,
        txHash: normalized,
        detectedAt: current.detectedAt ?? Date.now(),
        depositRail: 'cobo',
        source: 'cobo',
        coboAddress: session.watchAddress || current.coboAddress || current.watchAddress,
        privyAddress: session.privyAddress || current.privyAddress || '',
        completionExplorerUrl:
          session.completionExplorerUrl ||
          current.completionExplorerUrl ||
          resolveDepositExplorerUrl(resolveDefaultChainId()),
      });
      setFocusIfIdle(current.id);
      ensureCoboOrderPoller();
      void tickCoboDepositOrders();
      startReceiptMonitorForItem(getItemById(current.id) ?? current);
      reconcileIncomingPollPhase();
      if (!skipSync) syncDepositUiVisibility();
      return current.id;
    }
    // 异 tx：继续往下走 addItem 替换，旧笔由 addItem 写入 seen
  }

  const existing = getItemByTxHash(hash);
  if (existing) {
    // 已知 tx：绝不回退终态，不重复进入 depositing
    if (isItemTerminal(existing.phase)) {
      if (existing.navDismissed && existing.embeddedSeen) {
        return existing.id;
      }
      return existing.id;
    }
    if (existing.phase === ITEM_PHASE.DEPOSITING) {
      if (session.depositRail === 'cobo' || isCoboDepositItem(existing)) {
        ensureCoboOrderPoller();
      }
      return existing.id;
    }
    const coboRail = session.depositRail === 'cobo';
    patchItem(existing.id, {
      phase: ITEM_PHASE.DEPOSITING,
      amount,
      fromAddress: from,
      txHash: normalized || hash,
      detectedAt: existing.detectedAt ?? Date.now(),
      ...(coboRail
        ? {
            depositRail: 'cobo',
            source: 'cobo',
            coboAddress: session.watchAddress,
            privyAddress: session.privyAddress || existing.privyAddress || '',
            completionExplorerUrl:
              session.completionExplorerUrl ||
              existing.completionExplorerUrl ||
              resolveDepositExplorerUrl(resolveDefaultChainId()),
          }
        : {}),
    });
    setFocusIfIdle(existing.id);
    if (coboRail) {
      ensureCoboOrderPoller();
    }
    reconcileIncomingPollPhase();
    if (!skipSync) syncDepositUiVisibility();
    return existing.id;
  }

  const detectedAt = Date.now();
  const coboRail = session.depositRail === 'cobo';
  const item = buildDepositItem({
    phase: ITEM_PHASE.DEPOSITING,
    txHash: normalized || hash,
    amount,
    tokenSymbol: normalizeDepositTokenSymbol(session.tokenSymbol) || 'USDC',
    watchAddress: session.watchAddress,
    fromAddress: from,
    chainId: session.chainId,
    explorerUrl: session.explorerUrl ?? resolveDepositExplorerUrl(session.chainId),
    completionExplorerUrl:
      session.completionExplorerUrl || resolveDepositExplorerUrl(resolveDefaultChainId()),
    startedWithDialogOpen: depositDialogOpen.value,
    uiSurface: resolveUiSurface(),
    source: coboRail ? 'cobo' : 'incoming',
    depositRail: coboRail ? 'cobo' : 'direct',
    coboAddress: coboRail ? session.watchAddress : '',
    privyAddress: session.privyAddress || '',
    detectedAt,
  });

  addItem(item);
  trackDepositTransferSubmittedOnce(item, { via: 'watcher' });
  setFocusIfIdle(item.id);
  startReceiptMonitorForItem(item);
  if (coboRail) {
    ensureCoboOrderPoller();
    void tickCoboDepositOrders();
  }
  reconcileIncomingPollPhase();
  if (!skipSync) syncDepositUiVisibility();
  return item.id;
}

function upsertItemsFromIncomingBatch(detections, session) {
  if (!detections.length || !hasAuthSession()) return;
  // 同批多笔只跟最新一笔
  const latest = detections[detections.length - 1];
  upsertItemFromIncoming({ ...latest, session, skipSync: false });
}

/** 点击确认后、尚未拿到 txHash */
export function beginConnectedDepositSubmit(options = {}) {
  archiveStaleTerminalItems();

  const cid = options.chainId ?? resolveDefaultChainId();
  const item = buildDepositItem({
    phase: ITEM_PHASE.SUBMITTING,
    amount: options.amount,
    tokenSymbol: options.tokenSymbol ?? 'USDC',
    watchAddress: options.receivingAddress ?? options.watchAddress ?? '',
    fromAddress: options.fromAddress ?? '',
    chainId: cid,
    explorerUrl: resolveDepositExplorerUrl(cid),
    startedWithDialogOpen: depositDialogOpen.value,
    uiSurface: resolveUiSurface(),
    source: 'connected',
    // Connected 打到 Cobo：与邮箱手动转账同一条路，成功只认订单 status=5 / 平台 USD 到账
    depositRail: 'cobo',
    coboAddress: options.receivingAddress ?? options.watchAddress ?? '',
  });

  addItem(item);
  pinFocus(item.id);
  syncDepositUiVisibility();
  return item.id;
}

/** 拿到 txHash 后进入链上确认 */
export function confirmConnectedDepositItem(itemId, options = {}) {
  const hash = String(options.txHash ?? '').trim();
  if (!hash) return;

  const existing = getItemById(itemId);
  if (!existing) return;

  const dup = getItemByTxHash(hash);
  if (dup && dup.id !== itemId) {
    stopReceiptMonitor(itemId);
    patchItem(itemId, {
      navDisplayed: true,
      navDismissed: true,
      embeddedSeen: true,
    });
    pinFocus(dup.id);
    if (isItemInProgress(dup.phase)) {
      startReceiptMonitorForItem(dup);
    }
    syncDepositUiVisibility();
    return;
  }

  const item = patchItem(itemId, {
    ...(FUNDING_V2_ENABLED ? { depositRail: 'cobo' } : {}),
    phase: ITEM_PHASE.DEPOSITING,
    txHash: hash,
    amount: options.amount ?? existing?.amount,
    tokenSymbol: options.tokenSymbol ?? existing?.tokenSymbol,
    watchAddress: options.receivingAddress ?? options.watchAddress ?? existing?.watchAddress,
    fromAddress: options.fromAddress ?? existing?.fromAddress,
    chainId: options.chainId ?? existing?.chainId,
    explorerUrl:
      options.explorerUrl ??
      existing?.explorerUrl ??
      resolveDepositExplorerUrl(options.chainId ?? existing?.chainId),
  });

  if (!item) return;

  // 与弹窗 transfer_submitted 对齐去重键（弹窗已打则此处不再报 51）
  trackDepositTransferSubmittedOnce(item, {
    via: 'connected',
    txHash: hash,
    action: 'connected_transfer',
    amount: item.amount,
    token: item.tokenSymbol,
  });

  saveDepositWatchSession({
    mode: 'tx',
    txHash: hash,
    chainId: item.chainId,
    explorerUrl: item.explorerUrl,
    tokenSymbol: item.tokenSymbol,
    amount: item.amount,
    rpcUrl: resolveDepositRpcUrl(item.chainId),
    status: 'depositing',
    watchAddress: item.watchAddress,
    fromAddress: item.fromAddress,
    monitorWatchAddress: options.watchAddress ?? item.fromAddress,
    tokenContracts: normalizeTokenContracts(
      options.tokenContracts?.length ? options.tokenContracts : options.tokenContract
    ),
    tokenContract: normalizeTokenContracts(
      options.tokenContracts?.length ? options.tokenContracts : options.tokenContract
    )[0],
    decimals: options.decimals ?? 6,
    depositItemId: itemId,
  });

  setFocus(itemId);
  pinFocus(itemId);
  startReceiptMonitorForItem(item);
  // Connected→Cobo：不靠 receipt SUCCESS，立刻踢 orders 轮询等平台入账
  if (isCoboDepositItem(item)) {
    kickCoboOrderPoller();
  }
  syncDepositUiVisibility();
}

export function failConnectedDepositItem(itemId) {
  const item = getItemById(itemId);
  if (!item) return;
  applyItemPhase(itemId, ITEM_PHASE.FAILED);
}

export function focusDepositItem(itemId) {
  const item = getItemById(itemId);
  if (!item) return;
  if (isItemTerminal(item.phase) && item.navDismissed && item.embeddedSeen) return;

  manualFocusItemId = itemId;
  pinnedFocusItemId = null;
  focusItemId.value = itemId;
  syncDepositUiVisibility();
}

export function setDepositCompleteHandler(handler) {
  completeHandler = typeof handler === 'function' ? handler : null;
}

export function setDepositDialogOpen(open) {
  const nextOpen = Boolean(open);
  const wasOpen = depositDialogOpen.value;

  // 已是目标态：勿再 sync UI（法币 step 变化会反复调用，否则旧 Embedded 被重刷）
  if (nextOpen === wasOpen) return;

  if (nextOpen && !wasOpen) {
    setDepositChainDwellDialogOpen(true);
    // 先置 true，避免同 tick 内 syncDepositWatch 早于本函数尾部赋值导致 ensureCobo 空转
    depositDialogOpen.value = true;
    depositDialogClosing.value = false;
    dialogSelectNetworkReported = false;
    archiveStaleTerminalItems();
    // 开窗后若已有所选链（含默认链），补一次 10s 计时
    if (dialogSelectedNetwork?.chain) {
      armDepositChainDwell(dialogSelectedNetwork);
    }
    // 打开充值弹窗：右上角进行中状态收回弹窗页脚 Embedded（已点叉的同笔不再拉起）
    for (const item of depositItems.value) {
      if (!isItemInProgress(item.phase)) continue;
      if (item.navDismissed && item.embeddedSeen) continue;
      patchItem(item.id, {
        uiSurface: 'embedded',
        embeddedSeen: false,
        navDismissed: false,
      });
    }
    const nav = pickNavItem(depositItems.value, getFocusItem());
    if (nav && isItemInProgress(nav.phase)) {
      patchItem(nav.id, { navDisplayed: true });
    }
  }

  if (!nextOpen && wasOpen) {
    setDepositChainDwellDialogOpen(false);
    // 关窗且尚未因入账状态卡报过 → 上报当前所选链
    flushDepositSelectNetworkOnDialogClose();
    dialogSelectedNetwork = null;
    const wasEmbeddedShowing = embeddedVisible.value;
    const focusForAck = wasEmbeddedShowing ? getFocusItem() : null;

    depositDialogClosing.value = true;
    embeddedVisible.value = false;
    depositDialogOpen.value = false;
    depositWatchUrgent = false;
    // 关窗：持久化时间窗 + seen，ack Cobo 状态卡，停 orders（RPC 照旧）
    flushCoboSuppressOnDialogClose();

    // 进行中直转：交给右上角 Card；Cobo 已在 flush 里 ack
    for (const item of depositItems.value) {
      if (isCoboDepositItem(item)) continue;
      if (isItemInProgress(item.phase)) {
        if (item.navDismissed && item.embeddedSeen) {
          patchItem(item.id, { uiSurface: 'nav' });
          continue;
        }
        patchItem(item.id, {
          uiSurface: 'nav',
          embeddedSeen: true,
          navDismissed: false,
          navDisplayed: false,
        });
        continue;
      }
      if (focusForAck && item.id === focusForAck.id) {
        markItemAcknowledged(item);
      }
    }

    if (focusForAck) {
      clearPinnedFocus(focusForAck.id);
      focusItemId.value = pickFocusItemId(depositItems.value);
    }

    syncDepositUiVisibility();

    if (hasInProgressItems(depositItems.value)) {
      postDialogFastTicksLeft = POST_DIALOG_FAST_MAX_TICKS;
      reconcileIncomingPollPhase({ kick: true });
    } else {
      postDialogFastTicksLeft = 0;
      void restoreBootstrapDepositWatch();
    }
    return;
  }

  depositDialogOpen.value = nextOpen;
  syncDepositUiVisibility();
}

export function setDepositStatusEmbedAnchor(el) {
  if (!depositDialogOpen.value) {
    if (!el) embedAnchorEl.value = null;
    return;
  }
  // 主充值弹窗 ↔ 链上转账弹窗页脚锚点切换
  embedAnchorEl.value = el ?? null;
}

/** el-dialog @closed：动画结束后再清 anchor，避免 Teleport 在 leave 期间落到 body */
export function finalizeDepositDialogClose() {
  depositDialogClosing.value = false;
  embedAnchorEl.value = null;
}

export function dismissEmbeddedDepositStatus() {
  const focus = getFocusItem();
  if (focus) {
    markItemAcknowledged(focus);
    clearPinnedFocus(focus.id);
  }
  embeddedVisible.value = false;
  focusItemId.value = pickFocusItemId(depositItems.value);
  syncDepositUiVisibility();
}

/** @deprecated */
export function dismissDepositStatus() {
  dismissNavDepositStatus();
}

function persistIncomingSession(base, patch = {}) {
  saveDepositWatchSession({ ...base, ...patch, mode: 'incoming' });
}

function resolveSessionContracts(session) {
  const fromList = normalizeTokenContracts(session?.tokenContracts);
  if (fromList.length) return fromList;
  const single = String(session?.tokenContract ?? '').trim();
  return single ? [single] : [];
}

function runIncomingMonitor(session, handlers = {}) {
  if (FUNDING_V2_ENABLED) {
    ensureBackendDepositWatch();
    return;
  }
  const monitorAuthGen = getAuthGeneration();
  const tokenContracts = resolveSessionContracts(session);
  const persistent = Boolean(session.persistent);
  const monitor = pollIncomingTransfer({
    rpcUrl: session.rpcUrl,
    tokenContracts,
    watchAddress: session.watchAddress,
    fromBlock: session.fromBlock,
    lastScannedBlock: session.lastScannedBlock ?? null,
    decimals: session.decimals ?? 6,
    initialPhase: session.pollPhase ?? POLL_PHASE.SLOW,
    lastActivityAt: session.lastActivityAt ?? Date.now(),
    seenTx: session.seenTx ?? [],
    persistent,
    onDetectedBatch: (detections) => {
      if (!isDepositWatchAllowed(monitorAuthGen)) return;
      // 弹窗开着且非钱包直转急扫：Cobo/法币面只记 seen，不弹直转状态卡；并保持 SLOW
      if (depositDialogOpen.value && !depositWatchUrgent) {
        for (const detected of detections) {
          if (detected?.txHash) appendTxToIncomingSeenTx(detected.txHash);
        }
        persistIncomingSession(session, {
          pollPhase: POLL_PHASE.SLOW,
          lastActivityAt: Date.now(),
        });
        applyIncomingPollPhase(POLL_PHASE.SLOW, { kick: false });
        return;
      }
      upsertItemsFromIncomingBatch(detections, session);
      const last = detections[detections.length - 1];
      if (last) {
        persistIncomingSession(session, {
          status: 'depositing',
          txHash: last.txHash,
          fromAddress: last.fromAddress,
          amount: last.amount,
          pollPhase: depositWatchUrgent ? POLL_PHASE.FAST : POLL_PHASE.SLOW,
          lastActivityAt: Date.now(),
        });
      }
      if (!depositWatchUrgent) {
        applyIncomingPollPhase(POLL_PHASE.SLOW, { kick: false });
      }
      handlers.onDetected?.(detections);
    },
    onPhaseChange: (pollPhase, lastActivityAt) => {
      if (!isDepositWatchAllowed(monitorAuthGen)) return;
      persistIncomingSession(session, { pollPhase, lastActivityAt });
    },
    onBlockScanned: (lastScannedBlock) => {
      if (!isDepositWatchAllowed(monitorAuthGen)) return;
      persistIncomingSession(session, { lastScannedBlock });
    },
    onReturnToSlow: () => {
      if (!isDepositWatchAllowed(monitorAuthGen)) return;
      persistIncomingSession(session, {
        status: 'watching',
        pollPhase: POLL_PHASE.SLOW,
      });
      handlers.onReturnToSlow?.();
    },
    onConfirmed: ({ amount, txHash: hash, fromAddress: from, blockTimestampMs } = {}) => {
      if (!isDepositWatchAllowed(monitorAuthGen)) return;
      const normalized = String(hash ?? '').trim();
      const existing = getItemByTxHash(normalized);
      const coboRail = session.depositRail === 'cobo' || isCoboDepositItem(existing);
      const chainTime =
        Number.isFinite(Number(blockTimestampMs)) && Number(blockTimestampMs) > 0
          ? Number(blockTimestampMs)
          : null;

      if (coboRail) {
        if (existing) {
          markCoboOnChainConfirmed(existing.id, {
            amount,
            fromAddress: from ?? existing.fromAddress,
            txHash: normalized,
            blockTimestampMs: chainTime,
          });
        } else {
          const item = buildDepositItem({
            phase: ITEM_PHASE.DEPOSITING,
            txHash: normalized,
            amount,
            tokenSymbol: session.tokenSymbol,
            watchAddress: session.watchAddress,
            fromAddress: from,
            chainId: session.chainId,
            explorerUrl: session.explorerUrl,
            completionExplorerUrl:
              session.completionExplorerUrl || resolveDepositExplorerUrl(resolveDefaultChainId()),
            startedWithDialogOpen: depositDialogOpen.value,
            uiSurface: resolveUiSurface(),
            source: 'cobo',
            depositRail: 'cobo',
            coboAddress: session.watchAddress,
            privyAddress: session.privyAddress || '',
            coboOnChainConfirmed: true,
            detectedAt: Date.now(),
            depositConfirmedAt: chainTime,
          });
          addItem(item);
          trackDepositTransferSubmittedOnce(item, { via: 'watcher' });
          trackDepositChainConfirmedOnce(item, { via: 'cobo_on_chain' });
          appendTxToIncomingSeenTx(normalized);
          setFocusIfIdle(item.id);
          ensureCoboOrderPoller();
          syncDepositUiVisibility();
          if (!chainTime) void hydrateItemChainTimes(item.id);
        }
      } else if (existing) {
        applyItemPhase(existing.id, ITEM_PHASE.SUCCESS, {
          amount,
          fromAddress: from ?? existing.fromAddress,
          txHash: normalized,
          ...(chainTime ? { depositConfirmedAt: chainTime, confirmedAt: chainTime } : {}),
        });
        if (!chainTime) void hydrateItemChainTimes(existing.id);
      } else {
        const item = buildDepositItem({
          phase: ITEM_PHASE.SUCCESS,
          txHash: normalized,
          amount,
          tokenSymbol: session.tokenSymbol,
          watchAddress: session.watchAddress,
          fromAddress: from,
          chainId: session.chainId,
          explorerUrl: session.explorerUrl,
          startedWithDialogOpen: depositDialogOpen.value,
          uiSurface: resolveUiSurface(),
          source: 'incoming',
          depositRail: 'direct',
          depositConfirmedAt: chainTime,
          confirmedAt: chainTime,
        });
        addItem(item);
        appendTxToIncomingSeenTx(normalized);
        setFocusIfIdle(item.id);
        syncDepositUiVisibility();
        if (!chainTime) void hydrateItemChainTimes(item.id);
      }

      const state = incomingGetState?.() ?? {};
      persistIncomingSession(session, {
        status: persistent ? 'watching' : 'tail',
        pollPhase: POLL_PHASE.TAIL,
        lastActivityAt: Date.now(),
        seenTx: state.seenTx ?? session.seenTx,
        lastScannedBlock: state.lastScannedBlock ?? session.lastScannedBlock,
        amount,
        txHash: hash,
        fromAddress: from ?? session.fromAddress,
      });
      handlers.onConfirmed?.({ amount, txHash: hash, fromAddress: from });
    },
    onFailed: ({ txHash: hash, fromAddress: from }) => {
      if (!isDepositWatchAllowed(monitorAuthGen)) return;
      const existing = getItemByTxHash(hash);
      if (existing) {
        applyItemPhase(existing.id, ITEM_PHASE.FAILED, {
          fromAddress: from ?? existing.fromAddress,
          txHash: hash,
        });
      }
      const state = incomingGetState?.() ?? {};
      persistIncomingSession(session, {
        status: persistent ? 'watching' : 'tail',
        pollPhase: POLL_PHASE.TAIL,
        lastActivityAt: Date.now(),
        seenTx: state.seenTx ?? session.seenTx,
        lastScannedBlock: state.lastScannedBlock ?? session.lastScannedBlock,
        txHash: hash,
        fromAddress: from ?? session.fromAddress,
      });
      handlers.onFailed?.({ txHash: hash, fromAddress: from });
    },
    onIdleStop: () => {
      if (persistent) return;
      if (!isDepositWatchAllowed(monitorAuthGen)) return;
      clearDepositWatchSession();
      stopIncomingMonitor();
      activeWatchKey = '';
      handlers.onIdleStop?.();
    },
    onTimeout: () => {
      if (persistent) return;
      if (!isDepositWatchAllowed(monitorAuthGen)) return;
      clearDepositWatchSession();
      stopIncomingMonitor();
      activeWatchKey = '';
      handlers.onTimeout?.();
    },
    onTick: () => {
      if (!isDepositWatchAllowed(monitorAuthGen)) return;
      onIncomingMonitorTick();
      handlers.onTick?.();
    },
  });

  incomingStop = monitor.stop;
  incomingGetState = monitor.getState;
  incomingSetPhase = monitor.setPhase;
  incomingMarkSeen = monitor.markSeen;
}

export async function startDepositIncomingWatch(options = {}) {
  if (FUNDING_V2_ENABLED) {
    ensureBackendDepositWatch();
    return;
  }
  // Cobo 扫码：只轮询订单（独立 coboOrderWatch，不碰 RPC 会话）
  if (options.depositRail === 'cobo') {
    return startCoboDepositOrderWatch(options);
  }

  if (!hasAuthSession()) return;

  const startedGen = getAuthGeneration();
  const watchAddressVal = String(options.watchAddress ?? '').trim();
  const tokenContracts = normalizeTokenContracts(
    options.tokenContracts?.length ? options.tokenContracts : options.tokenContract
  );
  const cid = options.chainId ?? resolveDefaultChainId();
  const token = String(options.tokenSymbol ?? 'USDC').trim() || 'USDC';
  const decimals = options.decimals ?? 6;
  const persistent = options.persistent !== false;
  const watchKey = buildDepositWatchKey(watchAddressVal, tokenContracts);
  const anchorMode = options.anchorFromBlock === 'latest' ? 'latest' : 'lookback';

  if (!watchAddressVal || !tokenContracts.length) return;

  scrubLegacyCoboRailFromRpcSession();

  const existing = loadDepositWatchSession();
  const sameAddress =
    String(existing?.watchAddress ?? '').toLowerCase() === watchAddressVal.toLowerCase();
  const existingContracts = resolveSessionContracts(existing);
  const sameContracts = buildDepositWatchKey(watchAddressVal, existingContracts) === watchKey;

  const preferFast = Boolean(options.preferFast);
  const immediateTick = options.immediateTick !== false;

  if (watchKey === activeWatchKey && incomingStop) {
    const nextToken = normalizeDepositTokenSymbol(token) || 'USDC';
    const live = loadDepositWatchSession();
    if (live) {
      const patch = {};
      if (normalizeDepositTokenSymbol(live.tokenSymbol) !== nextToken) {
        patch.tokenSymbol = nextToken;
      }
      if (Object.keys(patch).length) persistIncomingSession(live, patch);
      for (const item of depositItems.value) {
        if (!isItemInProgress(item.phase)) continue;
        if (
          String(item.watchAddress ?? '').toLowerCase() !== watchAddressVal.toLowerCase() &&
          String(item.coboAddress ?? '').toLowerCase() !== watchAddressVal.toLowerCase()
        ) {
          continue;
        }
        const itemPatch = {};
        if (normalizeDepositTokenSymbol(item.tokenSymbol) !== nextToken) {
          itemPatch.tokenSymbol = nextToken;
        }
        if (Object.keys(itemPatch).length) patchItem(item.id, itemPatch);
      }
      if (Object.keys(patch).length) syncDepositUiVisibility();
    }
    if (preferFast || options.initialPhase === POLL_PHASE.FAST) {
      if (depositWatchUrgent) {
        postDialogFastTicksLeft = 0;
      }
      applyIncomingPollPhase(POLL_PHASE.FAST, { kick: immediateTick });
    } else if (options.initialPhase && Object.values(POLL_PHASE).includes(options.initialPhase)) {
      applyIncomingPollPhase(options.initialPhase, { kick: false });
    }
    return;
  }

  const rpcUrl = resolveDepositRpcUrl(cid);
  let fromBlock = options.fromBlock ?? existing?.fromBlock;
  let lastScannedBlock = existing?.lastScannedBlock ?? null;
  let pollPhase =
    preferFast || options.initialPhase === POLL_PHASE.FAST
      ? POLL_PHASE.FAST
      : options.initialPhase ?? existing?.pollPhase ?? POLL_PHASE.SLOW;
  let lastActivityAt = existing?.lastActivityAt ?? Date.now();
  let seenTx = mergeSeenTxLists(
    Array.isArray(existing?.seenTx) ? existing.seenTx : [],
    collectTxHashesFromItems(depositItems.value)
  );
  // 本会话起点：仅「当前已在跑的同一 watch」沿用，避免永久沿用昨天的起点扫进旧单
  const watchStartedAt =
    watchKey === activeWatchKey && Number(existing?.watchStartedAt) > 0
      ? Number(existing.watchStartedAt)
      : Date.now() - 60_000;

  async function resolveAnchorFromBlock() {
    if (anchorMode === 'latest') {
      try {
        return await fetchBlockNumber(rpcUrl);
      } catch (err) {
        console.warn('[deposit-status] fetch latest fromBlock failed', err);
        return 'latest';
      }
    }
    try {
      return await fetchFromBlockWithLookback(rpcUrl);
    } catch (err) {
      console.warn('[deposit-status] fetch fromBlock failed', err);
      return 'latest';
    }
  }

  if (!fromBlock) {
    if (sameAddress && existing?.fromBlock && anchorMode !== 'latest') {
      fromBlock = existing.fromBlock;
    } else {
      fromBlock = await resolveAnchorFromBlock();
    }
  }

  if (!isDepositWatchAllowed(startedGen)) return;

  if (watchKey !== activeWatchKey) {
    stopIncomingMonitor();
  }

  if (!sameAddress || !existing?.fromBlock || anchorMode === 'latest') {
    pollPhase =
      preferFast || options.initialPhase === POLL_PHASE.FAST
        ? POLL_PHASE.FAST
        : options.initialPhase ?? POLL_PHASE.SLOW;
    lastActivityAt = Date.now();
    seenTx = mergeSeenTxLists(collectTxHashesFromItems(depositItems.value));
    lastScannedBlock =
      anchorMode === 'latest' ? null : existing?.lastScannedBlock ?? lastScannedBlock;
    if (!options.fromBlock) {
      fromBlock = await resolveAnchorFromBlock();
    }
  } else if (!sameContracts) {
    pollPhase =
      preferFast || options.initialPhase === POLL_PHASE.FAST ? POLL_PHASE.FAST : POLL_PHASE.SLOW;
    lastActivityAt = Date.now();
    seenTx = mergeSeenTxLists(seenTx, collectTxHashesFromItems(depositItems.value));
  }

  if (!isDepositWatchAllowed(startedGen)) return;

  activeWatchKey = watchKey;

  const depositRail = 'direct';
  const privyAddress = String(options.privyAddress ?? '').trim();
  const explorerUrl = String(options.explorerUrl ?? '').trim() || resolveDepositExplorerUrl(cid);
  const completionExplorerUrl =
    String(options.completionExplorerUrl ?? '').trim() ||
    resolveDepositExplorerUrl(resolveDefaultChainId());
  const normalizedToken = normalizeDepositTokenSymbol(token) || 'USDC';

  if (!isDepositWatchAllowed(startedGen)) return;

  const session = {
    mode: 'incoming',
    persistent,
    watchAddress: watchAddressVal,
    tokenContracts,
    tokenContract: tokenContracts[0] ?? '',
    chainId: cid,
    explorerUrl,
    completionExplorerUrl,
    tokenSymbol: normalizedToken,
    decimals,
    rpcUrl,
    fromBlock,
    lastScannedBlock,
    pollPhase,
    lastActivityAt,
    seenTx,
    status: pollPhase === POLL_PHASE.TAIL ? 'tail' : 'watching',
    depositRail,
    privyAddress,
    watchStartedAt,
  };
  persistIncomingSession(session);
  runIncomingMonitor(session);

  if (preferFast || depositWatchUrgent) {
    reconcileIncomingPollPhase({ kick: immediateTick });
  }
}

/**
 * Cobo 扫码充值：只 poll /cobo/deposit/orders（内存态 coboOrderWatch）。
 * 绝不 stopIncomingMonitor / 绝不写 RPC 的 deposit-chain-watch 会话。
 */
export async function startCoboDepositOrderWatch(options = {}) {
  if (FUNDING_V2_ENABLED) {
    backendNextPollAt = 0;
    ensureBackendDepositWatch();
    return;
  }
  if (!hasAuthSession()) return;

  const startedGen = getAuthGeneration();
  const watchAddressVal = String(options.watchAddress ?? '').trim();
  const watchAddresses = normalizeCoboAddressList([
    ...(Array.isArray(options.watchAddresses) ? options.watchAddresses : []),
    watchAddressVal,
  ]);
  if (!watchAddresses.length) return;

  const primaryAddress = watchAddressVal || watchAddresses[0];
  const token = normalizeDepositTokenSymbol(options.tokenSymbol) || 'USDC';
  const watchKey = `cobo-order:multi:${token}`;
  const existing = coboOrderWatch;
  const sameCobo = Boolean(existing) && existing.watchKey === watchKey;
  const suppress = loadCoboOrderSuppress();

  // 已在听 Cobo：合并地址列表 + 踢订单轮询（不碰 RPC）
  if (sameCobo && coboOrderPollTimer != null) {
    const mergedAddresses = normalizeCoboAddressList([
      ...(Array.isArray(existing.watchAddresses) ? existing.watchAddresses : []),
      existing.watchAddress,
      ...watchAddresses,
    ]);
    const patch = { watchAddresses: mergedAddresses };
    if (primaryAddress) patch.watchAddress = primaryAddress;
    if (options.tokenSymbol) {
      const nextToken = normalizeDepositTokenSymbol(options.tokenSymbol) || 'USDC';
      if (normalizeDepositTokenSymbol(existing.tokenSymbol) !== nextToken) {
        patch.tokenSymbol = nextToken;
      }
    }
    patchCoboOrderWatch(patch);
    kickCoboOrderPoller();
    return;
  }

  // 时间窗：优先用关窗留下的 notBeforeMs，避免再调 orders 把旧单当新单
  const suppressFloor = Math.max(0, Number(suppress.notBeforeMs) || 0);
  const watchStartedAt =
    sameCobo && Number(existing?.watchStartedAt) > 0
      ? Number(existing.watchStartedAt)
      : suppressFloor > 0
      ? suppressFloor
      : Date.now() - 60_000;

  let seenCoboOrderIds = [
    ...new Set([
      ...(sameCobo && Array.isArray(existing?.seenCoboOrderIds) ? existing.seenCoboOrderIds : []),
      ...suppress.seenOrderIds,
    ]),
  ];
  let seenTx = mergeSeenTxLists(
    sameCobo && Array.isArray(existing?.seenTx) ? existing.seenTx : [],
    suppress.seenTx
  );
  const seedAddresses = normalizeCoboAddressList([
    ...(sameCobo && Array.isArray(existing?.watchAddresses) ? existing.watchAddresses : []),
    sameCobo ? existing?.watchAddress : '',
    ...watchAddresses,
  ]);

  const cid = options.chainId ?? resolveDefaultChainId();
  const explorerUrl = String(options.explorerUrl ?? '').trim();
  const completionExplorerUrl =
    String(options.completionExplorerUrl ?? '').trim() ||
    resolveDepositExplorerUrl(resolveDefaultChainId());
  const privyAddress = String(options.privyAddress ?? '').trim();

  coboOrderWatch = {
    watchKey,
    watchAddress: primaryAddress,
    watchAddresses: seedAddresses,
    tokenSymbol: token,
    decimals: options.decimals ?? 6,
    chainId: cid,
    explorerUrl,
    completionExplorerUrl,
    privyAddress,
    watchStartedAt,
    seenTx,
    seenCoboOrderIds,
  };
  ensureCoboOrderPoller();

  try {
    const res = await coboDepositOrders({ pageNum: 1, pageSize: 10 });
    if (isDepositWatchAllowed(startedGen) && coboOrderWatch?.watchKey === watchKey) {
      const { rows } = normalizeCoboOrdersPage(res);
      const historicalIds = collectHistoricalCoboOrderIds(rows, seedAddresses, {
        notBeforeMs: watchStartedAt,
      });
      const historicalTx = collectHistoricalCoboDepositTxids(rows, seedAddresses, {
        notBeforeMs: watchStartedAt,
      });
      seenCoboOrderIds = [...new Set([...seenCoboOrderIds, ...historicalIds])];
      seenTx = mergeSeenTxLists(seenTx, historicalTx);
      patchCoboOrderWatch({ seenCoboOrderIds, seenTx });
      mergeCoboOrderSuppress({ orderIds: seenCoboOrderIds, txHashes: seenTx });
    }
  } catch (err) {
    console.warn('[deposit-status] seed historical cobo orders failed', err);
  }

  if (!isDepositWatchAllowed(startedGen)) return;
  if (!coboOrderWatch) return;

  void tickCoboDepositOrders();
}

/** connected 钱包：已有 txHash 时追加队列条目（兼容旧调用） */
export function watchDepositTx(options = {}) {
  const hash = String(options.txHash ?? '').trim();
  if (!hash) return;

  const cid = options.chainId ?? resolveDefaultChainId();
  const receivingAddress = String(options.receivingAddress ?? options.watchAddress ?? '').trim();

  let item = getItemByTxHash(hash);
  if (!item) {
    item = buildDepositItem({
      phase: ITEM_PHASE.DEPOSITING,
      txHash: hash,
      amount: options.amount,
      tokenSymbol: options.tokenSymbol ?? 'USDC',
      watchAddress: receivingAddress,
      fromAddress: options.fromAddress ?? '',
      chainId: cid,
      explorerUrl: resolveDepositExplorerUrl(cid),
      startedWithDialogOpen: depositDialogOpen.value,
      uiSurface: resolveUiSurface(),
      source: 'connected',
    });
    addItem(item);
  } else if (isItemInProgress(item.phase)) {
    patchItem(item.id, {
      phase: ITEM_PHASE.DEPOSITING,
      txHash: hash,
      amount: options.amount ?? item.amount,
    });
  }

  confirmConnectedDepositItem(item.id, options);
}

function restoreItemsFromSnapshot() {
  const snapshot = dedupeDepositItemsByTxHash(loadDepositItemsSnapshot());
  if (!snapshot.length) return;
  // 不恢复进行中的历史充值（未到账旧单），只恢复尚未关闭的终态提示
  const resumable = snapshot.filter((item) => FUNDING_V2_ENABLED || !isItemInProgress(item.phase));
  if (!resumable.length) {
    saveDepositItemsSnapshot([]);
    return;
  }
  replaceDepositItems(
    resumable.map((item) =>
      FUNDING_V2_ENABLED && isItemInProgress(item.phase) ? { ...item, depositRail: 'cobo' } : item
    )
  );
  focusItemId.value = depositItems.value[0]?.id ?? null;
}

async function reconcileRestoredDepositItems() {
  const items = [...depositItems.value];
  for (const item of items) {
    if (item.phase === ITEM_PHASE.DEPOSITING && item.txHash) {
      await reconcileItemPhaseWithChain(item);
    }
  }

  for (const item of depositItems.value) {
    const fresh = getItemById(item.id);
    if (fresh?.phase === ITEM_PHASE.DEPOSITING && fresh.txHash) {
      startReceiptMonitorForItem(fresh);
    }
  }
  syncDepositUiVisibility();
}

export async function resumeDepositWatch() {
  if (FUNDING_V2_ENABLED) {
    restoreItemsFromSnapshot();
    ensureBackendDepositWatch();
    return;
  }
  if (!hasAuthSession()) return;

  const startedGen = getAuthGeneration();
  restoreItemsFromSnapshot();
  await reconcileRestoredDepositItems();

  if (!isDepositWatchAllowed(startedGen)) return;

  const session = loadDepositWatchSession();
  if (!session) return;

  if (session.mode === 'incoming') {
    // 历史误把 cobo 写进 RPC 会话：清掉后走常驻 Pol
    if (session.depositRail === 'cobo') {
      clearDepositWatchSession();
      activeWatchKey = '';
      await bootstrapDepositWatchAfterLogin();
      return;
    }
    activeWatchKey = buildDepositWatchKey(session.watchAddress, resolveSessionContracts(session));
    const mergedSeen = mergeSeenTxLists(
      session.seenTx,
      collectTxHashesFromItems(depositItems.value)
    );
    runIncomingMonitor({
      ...session,
      seenTx: mergedSeen,
      persistent: session.persistent !== false,
    });
    return;
  }

  if (session.mode === 'tx' && session.txHash) {
    const existing = getItemByTxHash(session.txHash);
    if (existing) {
      if (existing.phase === ITEM_PHASE.DEPOSITING) {
        await reconcileItemPhaseWithChain(existing);
        const fresh = getItemById(existing.id);
        if (fresh?.phase === ITEM_PHASE.DEPOSITING) {
          startReceiptMonitorForItem(fresh);
        }
      }
    } else {
      watchDepositTx({
        txHash: session.txHash,
        chainId: session.chainId,
        tokenSymbol: session.tokenSymbol,
        amount: session.amount,
        watchAddress: session.monitorWatchAddress || session.watchAddress,
        receivingAddress: session.watchAddress,
        fromAddress: session.fromAddress,
        tokenContracts: resolveSessionContracts(session),
        decimals: session.decimals,
      });
    }
  }
}

export async function bootstrapDepositWatchAfterLogin() {
  if (FUNDING_V2_ENABLED) {
    ensureBackendDepositWatch();
    return;
  }
  if (!hasAuthSession()) return;
  if (bootstrapInflight) return bootstrapInflight;

  scrubLegacyCoboRailFromRpcSession();

  const startedGen = getAuthGeneration();

  bootstrapInflight = (async () => {
    try {
      if (!isDepositWatchAllowed(startedGen)) return;

      // 不依赖地址监听：先补扫近 24h 已完成充值埋点
      await backfillRecentCoboDepositAnalytics();
      if (!isDepositWatchAllowed(startedGen)) return;

      if (getWalletProviderId() === 'privy') {
        try {
          await ensurePrivyEmbeddedReady();
        } catch (err) {
          console.warn('[deposit-status] privy not ready for watch bootstrap', err);
        }
      }

      if (!isDepositWatchAllowed(startedGen)) return;

      let address = await resolveTradeUserAddress();
      if (!address) {
        await new Promise((resolve) => setTimeout(resolve, 1200));
        if (!isDepositWatchAllowed(startedGen)) return;
        address = await resolveTradeUserAddress();
      }
      if (!address || !isDepositWatchAllowed(startedGen)) return;

      const tokenContracts = resolveDepositWatchContracts({ tokenSymbol: 'USDC' });
      if (!tokenContracts.length) return;

      await startDepositIncomingWatch({
        watchAddress: address,
        tokenContracts,
        tokenSymbol: 'USDC',
        decimals: 6,
        chainId: resolveDefaultChainId(),
        persistent: true,
        anchorFromBlock: 'latest',
      });
    } finally {
      bootstrapInflight = null;
    }
  })();

  return bootstrapInflight;
}

async function restoreBootstrapDepositWatch() {
  if (!hasAuthSession() || depositDialogOpen.value) return;
  scrubLegacyCoboRailFromRpcSession();
  if (hasInProgressItems(depositItems.value)) {
    if (postDialogFastTicksLeft <= 0) {
      postDialogFastTicksLeft = POST_DIALOG_FAST_MAX_TICKS;
    }
    reconcileIncomingPollPhase({ kick: true });
    return;
  }
  await bootstrapDepositWatchAfterLogin();
}

export function stopDepositWatch() {
  backendNextPollAt = 0;
  stopCoboOrderPoller();
  clearCoboOrderWatch();
  bootstrapInflight = null;
  depositWatchUrgent = false;
  postDialogFastTicksLeft = 0;
  stopCoboDepositOrderWatch();
  clearCoboOrderSuppress();
  stopIncomingMonitor();
  stopAllReceiptMonitors();
  clearDepositWatchSession();
  activeWatchKey = '';
  hideAllDepositUi();
}

export function isDepositWatchActive() {
  return Boolean(incomingStop) || receiptStops.size > 0 || coboOrderPollTimer != null;
}

export function useDepositStatus() {
  return {
    navVisible,
    embeddedVisible,
    visible: navVisible,
    phase,
    amountLabel,
    tokenSymbol,
    watchAddress,
    fromAddress,
    txHash,
    detectedAt,
    confirmedAt,
    chainId,
    explorerUrl,
    focusPipeline,
    navPipeline,
    depositDialogOpen,
    depositDialogClosing,
    embedAnchorEl,
    depositCounts,
    queueItems,
    navItem,
    navPhase,
    navAmountLabel,
    navTokenSymbol,
    navWatchAddress,
    navTxHash,
    navDetectedAt,
    navConfirmedAt,
    navExplorerUrl,
    focusItemId,
    depositItems,
    dismissNavDepositStatus,
    dismissEmbeddedDepositStatus,
    dismissDepositStatus,
    focusDepositItem,
  };
}
